import { attachChatAutomation } from '../../tenant/chat_automation';
import { requestCorrection, correctionFragment } from '../../tenant/corrections';
import { showTenantSuggestions } from '../../tenant/suggestions';
import { attachMessageSpeech } from '../../tenant/message_speech';
import Rails from '@rails/ujs';
import consumer from "../.././channels/consumer"
import ChatMessageService from "./chat_message_service"
import NotificationService from "../notification_service";
import phraseSelectionAlert from "../../contents/phrase_selection_alert";
import JapaneseTextService from './japanese_text_service';
import {
  chineseChatSetupStillApplies,
  ensureChineseTextService as ensureChineseTextServiceFor
} from './load_chinese_text_service';
const { getOrderedMessageNodes } = require('./message_node_grouping');
import GuidedModeService from './guided_mode_service';
import chatMetricsClient from '../../services/ChatMetricsClient';
import ChatConnectionAdapter from './chat_connection_adapter';
import FlagCorrectionService from './flag_correction_service';
import {
  annotateTransliteration,
  clearTransliteration,
  normalizePronunciationCharacters,
  usesSegmentedReadingAid,
  usesTransliteration
} from '../../reading_aids/reading_aid_languages';
import {
  HIDEABLE_WORD_SELECTOR,
  LAYOUT_CONTAINER_SELECTOR,
  shouldHideAll
} from './challenge_toggle';
import audioStorageService from './audio_storage_service';
import { SLOW_AUDIO_SERVER_SPEED } from './slow_audio_service';

const { recoverWorkerError } = require('./worker_error_recovery');

const KEYBOARD_PREFERENCE_KEY = 'langua_keyboard_preferred';
// The turtle's speed, relative to the reply's own audio
const SLOW_REPLAY_RATE = 0.75;

class ChatService {
  constructor(
    chatId,
    chatOptions,
    chatElementContainer,
    chatElement,
    chatControls,
    chatForm,
    chatInput,
    chatTopicCategory,
    chatSubtopicCategory,
    chatTopic,
    chatSubmitBtn,
    popoverService,
    translationService,
    audioPlayerService,
    chatTopicService,
    textCorrectionService,
    correctionExplanationService,
    chatTranslationService,
    alternativeResponseService,
    suggestedReplyService,
    slowAudioService,
    translatorModalService,
    feedbackModalService,
    pronunciationCharacters
    ) {

    this.popoverService = popoverService;
    this.translationService = translationService;
    this.audioPlayerService = audioPlayerService;
    this.chatTopicService = chatTopicService;
    this.textCorrectionService = textCorrectionService;
    this.correctionExplanationService = correctionExplanationService;
    this.flagCorrectionService = new FlagCorrectionService(
      document.querySelector('meta[name="csrf-token"]')?.content
    );
    this.showCorrectionFlag = document.querySelector('.chat-content-w')?.dataset?.correctionsFlagEnabled === 'true';
    this.chatTranslationService = chatTranslationService;
    this.alternativeResponseService = alternativeResponseService;
    this.suggestedReplyService = suggestedReplyService;
    this.slowAudioService = slowAudioService;
    this.translatorModalService = translatorModalService;
    this.feedbackModalService = feedbackModalService;
    this.pronunciationCharacters = normalizePronunciationCharacters(pronunciationCharacters);

    this.chatOptions = chatOptions;

    this.language = chatOptions.language;
    this.languageCode = chatOptions.languageCode;
    this.dialectCode = chatOptions.dialectCode
    this.guidedMode = chatForm?.dataset.serverMediatedAi === 'true' ? false : chatOptions.guidedMode;

    this.chatId = chatId;
    this.chatElementContainer = chatElementContainer;
    this.chatElement = chatElement;
    this.chatControls = chatControls;
    this.chatForm = chatForm;
    this.destroyTenantMessageSpeech = attachMessageSpeech(chatForm, { player: this.audioPlayerService, mode: () => this.chatOptions.mode, speed: () => Number(this.chatOptions.speedMultiplier) || 1, onReplyEnded: () => this.audioPlayerService.emitPlayEnded() });
    this.chatInput = chatInput;
    this.tenantChatAutomation = attachChatAutomation(this);
    this.chatTopicCategory = chatTopicCategory;
    this.chatSubtopicCategory = chatSubtopicCategory;
    this.chatTopic = chatTopic;

    this.chatSubmitBtn = chatSubmitBtn;
    this.chatSubmitBtn.disabled = true;

    this.suggestionBtn = document.getElementById('js-chat-suggested-reply-btn');
    this.recordBtn = document.getElementById('js-record-query-btn');
    this.pauseBtn = document.getElementById('js-pause-recording-btn');
    this.inputShell = document.querySelector('.js-chat-input-shell');
    this.inputComposer = document.querySelector('.js-chat-input-composer');
    this.clearTextBtn = document.getElementById('js-chat-clear-text-btn');
    // LAN-584: while recording, the composer box shows the live transcript and
    // the secondary row a recording indicator; the pause button moves into the
    // composer and goes back to this spot afterwards.
    this.liveTranscriptBox = document.querySelector('.js-chat-input-live');
    this.recordingIndicator = document.querySelector('.js-chat-recording-indicator');
    this.recordingTimeLabel = document.querySelector('.js-chat-recording-time');
    this.pauseBtnHomeNext = this.pauseBtn ? this.pauseBtn.nextElementSibling : null;
    this.keyboardToggleVisible = this.loadKeyboardPreference();
    this.suggestions = {}

    this.chatMessageService = new ChatMessageService();
    this.chatMessageService.language = chatOptions.language;
    this.chatMessageService.audioPlayerService = this.audioPlayerService;

    this.isSelecting = false;
    this.isDragging = false;

    this.startChatBtn = document.querySelector('.js-start-chat-btn');

    if (this.chatOptions.messageCount === '0') {
      // we need this to get the user to interact with the chat so the first message audio can be played
      if (this.startChatBtn === null || this.startChatBtn.length === 0) {
        // If Worker is enabled, wait for it to be ready before starting chat
        if (this.chatOptions.workerEnabled) {
          this.waitForWorkerAndStartChat();
        } else {
          this.startChat();
        }
      }
    }

    this.isLoadingMessage = false;
    this.isRecording = false;
    this.currentActive = null;
    this.lastMessage = null; // Store the last message
    this.debug = this.chatOptions.debug;
    this.pendingAudioUrl = null;
    this.isReadingAidsOn = this.pronunciationCharacters && this.pronunciationCharacters !== 'disabled';
    this.shouldProcessText = usesSegmentedReadingAid(this.language);

    // Initialize guided mode service if enabled
    if (this.guidedMode) {
      this.guidedModeService = new GuidedModeService(chatId, this.language, this.chatOptions.voice, this.chatOptions.voiceProvider, this.chatOptions.targetLanguage, this.popoverService, element => this.applyTransliteration(element));
      this.lastProcessedMessageId = null;
      this.isWaitingForResponse = false;
    }
    // Set highlighting mode on audio player service
    if (this.audioPlayerService) {
      this.audioPlayerService.setHighlightingMode(this.chatOptions.highlightMode);
    }
    if (this.chatMessageService) {
      this.chatMessageService.setHighlightingMode(this.chatOptions.highlightMode);
    }
  }

  persistKeyboardPreference(preferred) {
    try {
      localStorage.setItem(KEYBOARD_PREFERENCE_KEY, preferred ? 'true' : 'false');
    } catch(e) {}
  }

  loadKeyboardPreference() {
    try {
      return localStorage.getItem(KEYBOARD_PREFERENCE_KEY) === 'true';
    } catch(e) {
      return false;
    }
  }

  init() {
    // websocket chat messagesetup
    this.setupChatChannel();

    // websocket notification setup
    this.setupNotificationChannel();

    // setup buttons for chat topic menu
    this.setupTopicListeners();

    // setup form event listeners, and challenge toggle handler
    this.setupFormSubmit();

    // setup document (chat ready) and element click event listeners
    this.setupEventListeners();

    // process message text
    this.setupInitalLoadMessageTextRendering();

    // Textarea auto-expand
    this.setupTextareaAutoExpand();
    this.setupInputBarState();

    // Check for existing messages and trigger guided mode if needed
    if (this.guidedMode) {
      this.checkForGuidedModeReply();
    }

    if (window.innerWidth < 768) {
      this.chatInput.scrollIntoView({ behavior: 'smooth' });
      this.scrollToBottom();
    }
  }

  refreshConnectionContext() {
    if (this.chatConnection && this.chatConnection.refreshContext) {
      this.chatConnection.refreshContext();
    }
  }

  setupChatChannel() {
    const that = this;
    const chatForm = document.getElementById('chat-form');

    this.chatConnection = new ChatConnectionAdapter({
      chatId: that.chatId,
      chatOptions: that.chatOptions,
      userId: chatForm?.dataset?.userId,
      railsApiUrl: chatForm?.dataset?.railsApiUrl,
      callbacks: {
        received: async function(data) {
        if (data.worker_error) {
          recoverWorkerError(that, data.message);
          return;
        }
        if (data['error']) {
          if (data['error_type'] === 'message_limit_reached') {
            that.removeChatLoader(that.chatElement);
            const modal = data['data'];
            if (modal) {
              that.chatControls.remove();
              const modalWithClass = modal.replace('<div class="modal fade"', '<div class="modal fade chat-message-limit-modal"');
              $('body').append(modalWithClass);
              $('.chat-message-limit-modal').modal('show');
            }
          } else {
            that.chatElement.insertAdjacentHTML('beforeend', `
              <div class="alert alert-danger js-error-message font-weight-bold" role="alert">
                ${I18n.t('javascript.chat.error_message_html')}
              </div>
            `);

            that.removeChatLoader(that.chatElement);

            const switchButton = that.chatElement.querySelector('.js-switch-model');
            if (switchButton) {
              switchButton.addEventListener('click', () => that.switchModel());
            }
          }

          return;
        } else if (data['type'] === 'user_chat_message') {
          // Track that we received the user message back
          if (data['message'] && data['message']['chat_message_id']) {
            // Use the real message ID from server for all future tracking
            const realMessageId = data['message']['chat_message_id'];
            chatMetricsClient.trackMessageReceived(that.chatId, realMessageId);
            
            // Store mapping for future use if needed
            that.currentMessageId = realMessageId;
          }
          
          let $emptyContainer = $('<div>');
          let $messageDataEl = $(data['message']['data']);
          $emptyContainer.append($messageDataEl);
          // Process the message text if it's Japanese or Chinese
          if (data['message'] && data['message']['full_message'] && usesSegmentedReadingAid(this.language)) {
            // we need to process the HTML that is returned for the User message (this is a string)
            // it needs to be processed to create a new DOM element that will be appended to the chat.
            let messageContentEl = $emptyContainer.find('.chat-message').get(0);
            that.processLocalChatMessageElement(messageContentEl, that.language);
            
            // readingAidMessage = await that.processMessageText(data['message']['full_message'], that.readingAidService);
            // if (readingAidMessage !== data['message']['full_message']) {
              
            //   // need to do all the DOM work to get the pronunciation view to work
            //   // $messageDataEl.find('.chat-message-detail').html(readingAidMessage);
            // }
            if (that.isPronounciatingOn) {
              const messageContentEl = messageDataEl.get(2);
              readingAidMessage = await that.processMessageText(data['message']['full_message'], that.readingAidService);
              if (readingAidMessage !== data['message']['full_message']) {
                that.processPronunciation(messageContentEl, that.language);
                // need to do all the DOM work to get the pronunciation view to work
                // messageDataEl.find('.chat-message-detail').html(readingAidMessage);
              }
            }
          }

          // Store the audio URL with the new message ID if we have pending audio
          if (that.pendingAudioUrl && data['message']['chat_message_id']) {
            const audioKey = `audioUrl_${data['message']['chat_message_id']}`;
            localStorage.setItem(audioKey, that.pendingAudioUrl);
            that.pendingAudioUrl = null;
          }

          // don't show message if first message is 'Hi' and loading message is still showing
          if (that.isLoadingMessage && data['message']['full_message'] === 'Hi') {
            return;
          }

          // append the new message to the chat
          $('.chat-content .chat-message:last').after($messageDataEl);
          $messageDataEl.each((_index, element) => { that.applyTransliteration(element); });

          // setup auto correction for the new message
          if (that.chatForm?.dataset.serverMediatedAi !== 'true' && that.chatOptions.autoCorrect && data['message']['full_message'] !== 'Hi') {
            const containerEl = $('.chat-content').find(`#chat-message-${data['message']['chat_message_id']}`);
            let correctionText = containerEl.next().find('.chat-message-content').data('correction');
            if (containerEl) {
              await that.handleChatCorrection(containerEl, correctionText)
              that.scrollToBottom();
            }
          } else {
            that.scrollToBottom();
          }

        } else if (data['type'] === 'translation') {
          // find the chat message with the id and update the content
          const containerEl = $('.chat-content').find(`#chat-message-${data['chat_message_id']}`)[0];
          that.handleTranslation(containerEl, data);
        } else if (data['type'] === 'suggested_reply') {

          if (data['suggestions']['error']) {
            console.log(data['suggestions']['error']);
            that.chatElement.insertAdjacentHTML('beforeend', `
              <div class="alert alert-danger js-error-message font-weight-bold" role="alert">
                ${data['suggestions']['error']} ${I18n.t('javascript.chat.errors_persist_contact')}
              </div>
            `);
            that.suggestionBtn.classList.remove('pulse');
            that.suggestionBtn.disabled = false;
          } else {
            const lastChatMessage = $('.chat-content .chat-message:not(.self):last');
            that.suggestions[lastChatMessage.data('chat-message-id')] = data
            that.handleSuggestedReply(data);
          }
        } else if (data['type'] === 'correction') {
          // find the chat message with the id and update the content
          const containerEl = $('.chat-content').find(`#chat-message-${data['chat_message_id']}`);
          await that.handleChatCorrection(containerEl, data);
        } else if (data['type'] === 'correction_explanation') {
          const containerEl = $('.chat-content').find(`#chat-message-${data['chat_message_id']}`);
          await that.handleCorrectionExplanation(containerEl, data['text']);
        } else if (data['type'] === 'alternative_response') {
          const containerEl = $('.chat-content').find(`#chat-message-${data['chat_message_id']}`);
          await that.handleAlternativeResponse(containerEl, data['text']);
        } else if (data['type'] === 'slow_audio') {
          let containerEl, slowAudioEl;
          const audioUrl = data['audio_url'];
          
          if (data['chat_message_id']) {
            containerEl = $('.chat-content').find(`#chat-message-${data['chat_message_id']}`);
            slowAudioEl = containerEl.find('.js-slow-audio-btn')[0];
          } else {
            slowAudioEl = document.querySelector('.js-slow-audio-btn loading');
          }

          if (!slowAudioEl) {
            return;
          }

          slowAudioEl.dataset.audioUrl = audioUrl;
          const playAudioEl = slowAudioEl.closest('.chat-message-audio')?.querySelector('.js-play-chat-audio-btn');
          if (data['playback_rate'] && playAudioEl) playAudioEl.dataset.playbackRate = data['playback_rate'];
          // Pass null as second param since we don't need normal speed URL when we already have the slow URL
          that.handleSlowAudio(slowAudioEl, null, audioUrl);
        } else if (data['type'] === 'audio') {
          // Track first audio chunk
          const messageId = data['chat_message_id'] || that.currentMessageId;
          if (messageId && !that.chatMessageService.firstAudioTracked) {
            chatMetricsClient.trackFirstAudioChunk(that.chatId, messageId);
            that.chatMessageService.firstAudioTracked = true;
          }
          // Store spokenText for word alignment (sanitized text from TTS provider)
          if (data['spokenText']) {
            that.chatMessageService.spokenText = data['spokenText'];
          }
          // Store TTS context for non-Latin language handling
          if (data['ttsContext']) {
            that.chatMessageService.ttsContext = data['ttsContext'];
          }
          that.chatMessageService.processAudio(data['audio']);
        } else if (data['type'] === 'timestamps') {
          // Handle separate timestamp messages from Cartesia
          that.chatMessageService.processTimestampMessage(data);
        } else if (data['type'] === 'final') {
          // Track message fully delivered
          const messageId = data['chat_message_id'] || that.currentMessageId;
          if (messageId) {
            chatMetricsClient.trackMessageDelivered(that.chatId, messageId);
            that.chatMessageService.firstAudioTracked = false; // Reset for next message
            that.currentMessageId = null; // Reset for next message
          }
          that.chatMessageService.updateAudioReceived(data['chat_message_id']);
        } else {
          // Housekeeping frames (the worker's heartbeat pong, ready on every
          // reconnect, debug, metadata) carry no chat message, or only a status
          // string. Scrolling for them pulled anyone reading back through a
          // chat to the bottom every few minutes.
          if (!data['message'] || typeof data['message'] !== 'object') {
            return;
          }

          that.scrollToBottom();
          // scroll so chat input is visible
          // if on mobile
          if (window.innerWidth < 768) {
            that.chatInput.scrollIntoView({ behavior: 'smooth' });
          }

          that.chatMessageService.chatElement = that.chatElement;
          // Dedicated replies carry text first; their owned audio is fetched after
          // the shared renderer has inserted the message and playback control.
          if (that.chatForm?.dataset.serverMediatedAi === 'true') {
            that.chatMessageService.textOnly = that.chatOptions.mode === 'text_only' || data.cartus_reply_audio !== true;
          } else if (that.chatOptions.mode === 'text_only') {
            that.chatMessageService.textOnly = true;
          }

          that.chatMessageService.addMessage(data['message']);
          that.chatMessageService.chatMessageId = data['message']['chat_message_id'];
          if (data.cartus_completed_reply === true) {
            that.tenantChatAutomation.completed(data['message']['chat_message_id']);
            if (data.cartus_reply_audio !== true) that.destroyTenantMessageSpeech.playReply?.(data['message']['chat_message_id']);
          }

          const chatMessageId = data['message']['chat_message_id'];
          const text = data['message']['full_message'];
          
          // Track message delivery for text-only mode
          if (that.chatOptions.mode === 'text_only' && chatMessageId) {
            chatMetricsClient.trackMessageDelivered(that.chatId, chatMessageId);
          }

          if (that.chatOptions.autoTranslate === "written") {
            that.chatTranslationService.fetchTranslation(chatMessageId, text, that.chatOptions.language)
            .catch((error) => {
              console.log('translation error', error);
              // Auto-translate failed - no button to remove in auto mode
            });
          }

          if (data['debug']) {
            document.querySelector('.chat-debug-container').innerHTML = data['debug']['data'];
          }
        }
      }.bind(this),
        connected: function() {
          that.restoreChatSubmitState();
        },
        disconnected: function() {
          that.chatSubmitBtn.disabled = true;
        }
      }
    });
  }

  setupNotificationChannel() {
    consumer.subscriptions.create({ channel: "NotificationChannel"}, {
      received(data) {
        let message = data["notification_data"]["message"];
        let notificationType = data["notification_data"]["notification_type"];

        const notification = new NotificationService({
          message: message,
          notificationType: notificationType
        })

        notification.show();
      }
    });
  };

  setupTopicListeners() {
    const that = this;

    // Event listener for main category buttons (e.g., 'Roleplay')
    $(document).on('click', '.js-main-chat-option-btn', function(event) {
      event.preventDefault();

      const topicCategory = $(this).data('topic-category');
      const subOptionsContainer = $('.js-sub-options-container');

      let categories = $(this).data('topics');
      if (topicCategory === 'general') {
        categories = [...categories];
      }

      that.chatTopicService.showSubCategories(topicCategory, categories, subOptionsContainer);
    });

    // Event listener for subcategory buttons within a main category
    $(document).on('click', '.js-sub-category-btn', function(event) {
      event.preventDefault();

      const topicCategory = $(this).data('topic-category');
      const topicsData = $(this).data('topics');
      const subtopicCategory = $(this).data('subtopic-category');
      const vocabSubtopic = (topicCategory === 'vocab' && subtopicCategory === 'practice_my_vocab_blend_into_chat') ||
          (topicCategory === 'vocab' && subtopicCategory === 'practice_my_vocab_word_by_word');
      const chatAboutAnythingSubtopic = (topicCategory === 'chat_about_anything' && subtopicCategory === 'my_life');

      if (topicCategory === 'my_interests' || subtopicCategory === 'my_interests') {
        const subOptionsContainer = $('.js-sub-options-container');

        if (topicsData === "undefined") {
          that.chatTopicService.addCategoryContent('my_interests')
        } else {
          that.chatTopicService.showSubCategories(subtopicCategory, topicsData, subOptionsContainer);
        }
      } else if (topicCategory === 'roleplay' || vocabSubtopic || chatAboutAnythingSubtopic) {
        that.chatTopicService.showSubOptions(topicCategory, subtopicCategory, topicsData);
      } else {
        that.setTopic(topicCategory, '', subtopicCategory, event);
      }
    });

    // Event listener for final topic selection
    $(document).on('click', '.js-sub-chat-option-btn', function(event) {
      event.preventDefault();

      const topic = $(this).data('topic');
      const topicCategory = $(this).data('topic-category');
      const subtopicCategory = $(this).data('subtopic-category');

      that.setTopic(topicCategory, subtopicCategory, topic, event);
    });
  }

  setupFormSubmit() {
    const that = this;

    that.chatInput.addEventListener("input", function(event) {
      if (that.isRecording) {
        return;
      }

      that.updateInputBarState();
    });

    // Clear text button — clears all text in the input
    if (that.clearTextBtn) {
      that.clearTextBtn.addEventListener('click', function(event) {
        event.preventDefault();
        const recorder = that.chatOptions && that.chatOptions.audioRecordingService;
        if (recorder && recorder.isRecording) {
          // Discard the recording; the typed draft stays.
          recorder.cancelRecording();
          that.updateInputBarState();
          return;
        }
        that.chatInput.value = '';
        that.chatInput.dispatchEvent(new Event('input', { bubbles: true }));
        that.chatInput.focus();
        that.updateInputBarState();
      });
    }

    that.chatInput.addEventListener("keypress", function(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        event.stopPropagation();

        that.handleSubmitForm(that, event);
      }
    });

    that.chatForm.addEventListener('submit', function(event) {
      if (event.chatServiceAlreadyProcessed === true) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      that.handleSubmitForm(that, event);
    });

    $(document).on('click', '.chat-challenge', function(event) {
      // Check if the event has already been handled
      if (event.handled !== true) {
        // Mark the event as handled
        event.handled = true;

        // toggle the challenge class to closest element with class chat-challenge
        event.preventDefault();
        event.stopPropagation();
        that.toggleChallengeClass(this);
      }
    });
  }

  destroyReadingAids() {
    const that = this;
    if (usesTransliteration(this.language)) {
      clearTransliteration(document.querySelector('.chat-content'));
      return;
    }
    const aiMessages = document.querySelectorAll('.chat-content .chat-message');
    aiMessages.forEach(async (message) => {
      const messageContentEl = message.querySelector('.chat-message-content');
      if (messageContentEl.querySelector('.pronunciation-message')) {
        messageContentEl.querySelector('.pronunciation-message').remove();
      }
      // move this to a separate function
      if (that.pronunciationCharacters === 'disabled') {
        // turn on the old detail view
        that.toggleDetailView(messageContentEl, true);
      }
    });
  }

  setupInitalLoadMessageTextRendering() {
    // using bind(this)- this can be the Class Instance.

    // for japanese, update the messages with segements
    // for chinese, update the messages with reading-aids
    // for other languages, return no changes.

    switch (this.language) {
      case 'japanese':
        // setup language specific services
        this.japaneseTextService = new JapaneseTextService(this.pronunciationCharacters);
        this.readingAidService = this.japaneseTextService;
        if (this.guidedMode && this.guidedModeService) {
          this.guidedModeService.setReadingAidService(this.readingAidService, this.isReadingAidsOn);
        }
        this.setupReadingAids();
        // for each message, augment for segments (behind the flag)
        // for each message, augment for reading-aids (if pronunciation is on)
        break;
      case 'chinese': {
        // Keep pinyin dictionaries out of the shared application entry. Chinese
        // chats load the same cached chunk used by flashcard reading aids.
        const setupToken = {};
        const chatRoot = this.chatElement;
        this.chineseSetupToken = setupToken;
        this.ensureChineseTextService().then(service => {
          if (!service || !chineseChatSetupStillApplies(this, setupToken, chatRoot)) return;
          if (this.guidedMode && this.guidedModeService) {
            this.guidedModeService.setReadingAidService(this.readingAidService, this.isReadingAidsOn);
          }
          // for each message, augment for reading-aids (if pronunciation is on)
          this.setupReadingAids();
        }).catch(() => {
          // Leave the server-rendered chat text intact; never log message text or
          // loader details from this user-facing fallback.
          console.warn('Unable to load Chinese chat reading aids');
        });
        break;
      }
      default:
        // Greek and Korean: a romanization under each word, when the aid is on.
        if (usesTransliteration(this.language)) {
          this.applyTransliteration(document.querySelector('.chat-content'));
        }
        return;
    }
  }

  // LAN-870. Show the romanization under each word inside `root`, if the
  // learner has the transliteration aid on. A no-op for every other language.
  applyTransliteration(root) {
    return annotateTransliteration(root, this.language, this.pronunciationCharacters);
  }

  async ensureChineseTextService() {
    const service = await ensureChineseTextServiceFor(this);
    if (!service && this.language === 'chinese') {
      // Keep existing chat DOM intact and omit loader details/message text.
      console.warn('Unable to load Chinese chat reading aids');
    }
    return service;
  }

  setupReadingAids() {
    // roles for this function
    // for each language, process specific steps
    // for japanese, process reading-aids if pronunciation is on
    // for japanese, process only segments if pronunciation is off
    // for chinese, process reading-aids if pronunciation is on
    // for reading-aids  
    const aiMessages = document.querySelectorAll('.chat-content .chat-message');
    aiMessages.forEach(async (chatMessageElement) => {
      // process pronunciation
      await this.processLocalChatMessageElement(chatMessageElement, this.language);
    });
  }

  setupEventListeners() {
    const that = this;

    // event listener for AI Chat mesasges
    document.addEventListener('chat:messageready', async (event) => {

      if (event.handled !== true) {
        event.handled = true;
        try {
          if (that.chatForm?.dataset.serverMediatedAi === 'true' ? that.chatMessageService.textOnly : that.chatOptions.mode === 'text_only') {
            that.removeChatLoader(that.chatElement);
            that.chatMessageService.showMessage();
            that.restoreChatSubmitState();
          } else {
            that.removeChatLoader(that.chatElement);
            that.chatMessageService.showMessage();
            that.restoreChatSubmitState();
            that.scrollToBottom();
            let player = await that.chatMessageService.playAudio(that.chatOptions.voiceSpeed);
            player.on('end', () => {
              that.audioPlayerService.emitPlayEnded()
            });

          }
          if (that.isReadingAidsOn) {
            if (usesSegmentedReadingAid(that.language)) {
              that.processLocalChatMessageElement(that.chatMessageService.chatMessageElement, that.language);
            } else {
              that.applyTransliteration(that.chatMessageService.chatMessageElement);
            }
          }
          // Check for guided mode reply after message is ready
          if (that.guidedMode) {
            that.checkForGuidedModeReply();
          }
        } catch (error) {
          that.removeChatLoader(that.chatElement);
          that.restoreChatSubmitState();
          that.scrollToBottom();
          that.chatElement.insertAdjacentHTML('beforeend', `<div class="alert alert-danger" role="alert">${error}</div>`);
        }
      }
    });

    $(document).on('click', '.js-start-chat-btn', function(event) {
      if (event.handled !== true) {
        // Mark the event as handled
        event.handled = true;

        that.removeStartChatContainer();
        that.startChat();
      }
    });

    $(document).on('click', '.chat-challenge', function(event) {
      // Check if the event has already been handled
      if (event.handled !== true) {
        // Mark the event as handled
        event.handled = true;

        // toggle the challenge class to closest element with class chat-challenge
        event.preventDefault();
        event.stopPropagation();
        that.toggleChallengeClass(this);
      }
    });

    $('body').on('mousedown', '.js-translatable-text', function(e) {
      that.isSelecting = true;
    });

    $('body').on('mousemove', '.js-translatable-text, .saved-word', function(e) {
      if (that.isSelecting) {
        that.isDragging = true;
      }
    });

    $('body').on('mouseup', '.js-translatable-text', function(e) {
      if (that.isDragging) {
        that.isSelecting = false;
        that.isDragging = false;

        e.stopPropagation();

        let currentPhrase;
        if (usesSegmentedReadingAid(that.language)) {
          // Get only the base text without ruby annotations
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          const container = document.createElement('div');
          container.appendChild(range.cloneContents());

          // Remove rt and rp tags but keep their parent ruby elements
          const rtElements = container.getElementsByTagName('rt');
          const rpElements = container.getElementsByTagName('rp');
          while (rtElements.length > 0) rtElements[0].remove();
          while (rpElements.length > 0) rpElements[0].remove();

          currentPhrase = container.textContent;
        } else {
          currentPhrase = window.getSelection().toString();
        }

        let selectedTextLength = currentPhrase.replace(/\s+/g, '').length;

        if (selectedTextLength > 0) {
          if (that.chatOptions.audioRecordingService.isRecording) {
            that.chatOptions.audioRecordingService.stopRecording();
          }
        }

        if (selectedTextLength > 0 && selectedTextLength <= 70) {
          e.preventDefault();
          e.stopPropagation();

          let currentWordEl = e.target;

          that.popoverService.showLoadingPopover(currentWordEl);
          that.translationService.translateSentence(currentPhrase, async (translatedData) => {
            // Callback function to execute after translation is done

            // Show the loading popover
            if (that.isReadingAidsOn) {
              await that.popoverService.updatePopoverContent(currentWordEl, translatedData, true, that.readingAidService);
            } else {
              await that.popoverService.updatePopoverContent(currentWordEl, translatedData, true);
            }
            currentWordEl.classList.remove('loading');
          });
        } else {
          if (selectedTextLength > 70) {
            let phraseSelectionAlert = require('./../../contents/phrase_selection_alert');

            phraseSelectionAlert();
          }
        }
      } else {
        that.isSelecting = false;
      }
    });

    $('body').on('click', '.js-translatable-text:not(.challenge):not(.translated-message .js-translatable-text)', async function(e) {
      e.preventDefault();
      e.stopPropagation();

      let currentWordEl = e.target;

      // Return early if clicked element is a ruby-related tag
      if (currentWordEl.tagName.toLowerCase() === 'rt' ||
          currentWordEl.tagName.toLowerCase() === 'rp') {
        return;
      }

      let currentSentence;

      if (usesSegmentedReadingAid(that.language)) {
        if (currentWordEl.classList.contains('js-translatable-text')) {
          currentSentence = currentWordEl.dataset.phrase ?  currentWordEl.dataset.phrase : that.getSentenceOfWord(currentWordEl);
        } else if (currentWordEl.tagName.toLowerCase() === 'ruby' || currentWordEl.classList.contains('py-chinese-item') || currentWordEl.classList.contains('py-result-item') || currentWordEl.classList.contains('py-pinyin-item')) {
          // First try to get data-phrase from the js-translatable-text element
          const translatableEl = currentWordEl.classList.contains('js-translatable-text') ?
            currentWordEl :
            currentWordEl.closest('.js-translatable-text');

          currentSentence = translatableEl && translatableEl.dataset.phrase;
        }

        // Fallback to translatedText if no data-phrase found
        if (!currentSentence) {
          const messageContent = currentWordEl.closest('.chat-message-content');
          if (messageContent && messageContent.dataset) {
            currentSentence = messageContent.dataset.translatedText;
          } else {
            console.warn('Could not find .chat-message-content element or dataset for translation', currentWordEl);
            // Try to get text content as fallback
            currentSentence = currentWordEl.textContent || '';
          }
        }
      } else {
        currentSentence = that.getSentenceOfWord(currentWordEl);
      }

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      if (that.currentActive != null && that.currentActive.classList.contains('active')) {
        that.popoverService.disposePopover(that.currentActive);
      }

      that.popoverService.showLoadingPopover(currentWordEl);
      that.currentActive = currentWordEl;

      if (usesSegmentedReadingAid(that.language)) {
        that.translationService.translateSentence(currentSentence, async (translatedData) => {
          if (that.readingAidService && that.isReadingAidsOn) {
            await that.popoverService.updatePopoverContent(currentWordEl, translatedData, true, that.readingAidService);
          } else {
            await that.popoverService.updatePopoverContent(currentWordEl, translatedData, true);
          }
          that.translatorModalService.hideModal();
          currentWordEl.classList.remove('loading');
        });
      } else {
        that.translationService.translateWord(currentWordEl, currentSentence, (translatedData) => {
          that.popoverService.updatePopoverContent(currentWordEl, translatedData);
          that.translatorModalService.hideModal();
          currentWordEl.classList.remove('loading');
        });
      }
    });

    $('body').on('click', '.js-play-chat-audio-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (e.handled !== true) {
        e.handled = true;

        const text = $(this).data('text');
        const language = that.chatOptions.language;

        // Check if this is an alternative response button
        const wordEl = $(this).hasClass('btn-listen-alternative') ? 
          $(this).closest('.chat-alternative-response')[0] : 
          $(this).closest('.word-container')[0];
        
        const playbackRate = parseFloat(this.dataset.playbackRate) || 1.0;

        that.playAudioHandler(wordEl, this, text, language, playbackRate);
      }
    });

    $('body').on('click', '.js-slow-audio-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (e.handled !== true) {
        e.handled = true;
        this.classList.add('pulse')
        this.classList.add('loading');

        that.handleSlowAudio(this, this.dataset.normalSpeedAudioUrl, this.dataset.audioUrl);
      }
    });

    $('body').on('click', '.js-chat-correction-btn', async function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      // Disable button to prevent multiple clicks
      $(this).prop('disabled', true);

      if (e.handled !== true) {
        e.handled = true;

        const containerEl = $(this).closest('.chat-message');
        let correctionText = containerEl.next().find('.chat-message-content').data('correction');

        await that.handleChatCorrection(containerEl, correctionText);
      }
    });

    $('body').on('click', '.js-chat-correction-explanation-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      // Disable button to prevent multiple clicks
      $(this).prop('disabled', true);

      if (e.handled !== true) {
        e.handled = true;

        const containerEl = $(this).closest('.chat-message');
        let correctionExplanationText = that.chatForm?.dataset.serverMediatedAi === 'true' ? containerEl.find('.chat-correction-explanations').text() : containerEl.next().find('.chat-message-content').data('correction-explanation');
        const correctionExplanationBtn = this;
        correctionExplanationBtn.classList.add('pulse');

        that.handleCorrectionExplanation(containerEl, correctionExplanationText);
      }
    });

    $('body').on('click', '.js-chat-correction-flag-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const correctionsEl = $(this).closest('.chat-corrections');
      correctionsEl.find('.js-chat-correction-flag-panel').removeClass('d-none');
      $(this).addClass('d-none');
    });

    $('body').on('click', '.js-chat-correction-flag-cancel', function(e) {
      e.preventDefault();
      e.stopPropagation();
      const correctionsEl = $(this).closest('.chat-corrections');
      correctionsEl.find('.js-chat-correction-flag-panel').addClass('d-none');
      correctionsEl.find('.js-chat-correction-flag-btn').removeClass('d-none');
    });

    $('body').on('click', '.js-chat-correction-flag-submit', async function(e) {
      e.preventDefault();
      e.stopPropagation();

      const submitBtn = $(this);
      if (submitBtn.prop('disabled')) return;
      submitBtn.prop('disabled', true);

      const correctionsEl = submitBtn.closest('.chat-corrections');
      const chatMessageId = correctionsEl.closest('.chat-message-content').data('chat-message-id');

      try {
        const result = await that.flagCorrectionService.flagCorrection(chatMessageId);
        if (result && result.success) {
          correctionsEl.find('.js-chat-correction-flag-panel').addClass('d-none');
          const toast = correctionsEl.find('.js-chat-correction-flag-toast');
          toast.addClass('fade-in');
          setTimeout(() => toast.removeClass('fade-in'), 1100);
        } else {
          submitBtn.prop('disabled', false);
        }
      } catch (err) {
        console.error('Failed to flag correction:', err);
        submitBtn.prop('disabled', false);
      }
    });

    $('body').on('click', '.js-chat-translation-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      // Disable button to prevent multiple clicks
      $(this).prop('disabled', true);

      if (e.handled !== true) {
        e.handled = true;

        const translationBtn = this;
        translationBtn.classList.add('pulse');
        const chatMessageContentEl = this.parentNode.parentNode.querySelector('.chat-message-content');
        
        if (!chatMessageContentEl || !chatMessageContentEl.dataset) {
          console.error('Could not find chat message content element for translation button', this);
          translationBtn.classList.remove('pulse');
          return;
        }
        
        const chatMessageId = chatMessageContentEl.dataset.chatMessageId;
        const text = this.dataset.text

        that.chatTranslationService.fetchTranslation(chatMessageId, text, that.chatOptions.language)
          .catch((error) => {
            console.log('translation error', error);
            translationBtn.remove();
          });

      }
    });

    $('body').on('click', '.js-chat-suggested-reply-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      
      if (e.handled !== true) {
        e.handled = true;
        const lastChatMessage = $('.chat-content .chat-message:not(.self):not(.loading):last');
        const suggestionData = that.suggestions[lastChatMessage.data('chat-message-id')];
        // what is happening here? 
        if (lastChatMessage.length > 0) {
          that.audioPlayerService.emitPlayStarted();
          that.handleSuggestedReply(suggestionData);
        }
      }
    });

    $('body').on('click', '.js-chat-feedback-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (e.handled !== true) {
        e.handled = true;
        that.feedbackModalService.openModal();
      }
    });

    $('body').on('click', '.js-chat-translator-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (e.handled !== true) {
        e.handled = true;

        // Close dropdown menu if translator was triggered from inside it
        const dropdown = $(this).closest('.dropdown');
        if (dropdown.length) {
          dropdown.find('.dropdown-menu').removeClass('show');
          dropdown.find('[data-toggle="dropdown"]').attr('aria-expanded', 'false');
          dropdown.removeClass('show');
        }

        that.translatorModalService.chatService = that;
        that.translatorModalService.audioPlayerService = that.audioPlayerService;
        that.translatorModalService.readingAidService = that.readingAidService;
        that.translatorModalService.isReadingAidsOn = that.isReadingAidsOn;
        that.translatorModalService.openModal();

        if (that.currentActive && that.currentActive.classList.contains('active')) {
          that.popoverService.disposePopover(that.currentActive);
        }
      }
    });

    $('body').on('click', '.js-say-it-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (e.handled !== true) {
        e.handled = true;
        const button = $(this);
        
        if (button.hasClass('is-speaking')) {
          button.removeClass('is-speaking pulse');

          if (button.hasClass('btn-pronunciation')) {
            button.addClass('btn-success');
            button.css({'background-color': '#4CAF50', 'border-color': '#4CAF50'});
            button.html('<i class="fa fa-check"></i>');
            
            setTimeout(() => {
              const text = button.data('text');
              $('#chatSuggestionsModal').modal('hide');
              that.sendMessage(text, e);
            }, 2000);
          } else {
            button.html('<i class="fa fa-spinner fa-spin"></i>');

            setTimeout(() => {
              button.removeClass('is-speaking pulse');
              button.addClass('btn-success');
              button.css({'background-color': '#4CAF50', 'border-color': '#4CAF50'});
              button.html('<i class="fa fa-check"></i>');
            }, 2000);
          }
        } else if (!button.hasClass('btn-success')) {
          if (that.chatOptions.audioRecordingService.isRecording) {
            that.chatOptions.audioRecordingService.stopRecording();
          }

          button.addClass('is-speaking pulse');

          if (button.hasClass('btn-pronunciation')) {
            button.html(`<i class="fa fa-stop"></i> ${I18n.t('javascript.chat.stop_and_send')}`);
          } else {
            button.html(`<i class="fa fa-stop"></i> ${I18n.t('javascript.chat.stop')}`);
          }
        }
      }
    });

    $('body').on('click', '.js-user-chat-translation-btn', function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      // Disable button to prevent multiple clicks
      $(this).prop('disabled', true);

      if (e.handled !== true) {
        e.handled = true;

        const translationBtn = this;
        translationBtn.classList.add('pulse');
        const chatMessageContentEl = this.parentNode.parentNode.querySelector('.chat-message-content');
        
        if (!chatMessageContentEl || !chatMessageContentEl.dataset) {
          console.error('Could not find chat message content element for translation button', this);
          translationBtn.classList.remove('pulse');
          return;
        }
        
        const chatMessageId = chatMessageContentEl.dataset.chatMessageId;
        const text = this.dataset.text

        that.chatTranslationService.fetchTranslation(chatMessageId, text, that.chatOptions.language)
          .catch((error) => {
            console.log('translation error', error);
            translationBtn.remove();
          });
      }
    });

    $('body').on('click', '.js-alternative-response-btn', async function(e) {
      e.preventDefault();
      e.stopPropagation();

      if (that.chatOptions.audioRecordingService.isRecording) {
        that.chatOptions.audioRecordingService.stopRecording();
      }

      // Disable button to prevent multiple clicks
      $(this).prop('disabled', true);

      if (e.handled !== true) {
        e.handled = true;

        const containerEl = $(this).closest('.chat-message');
        let text = that.chatForm?.dataset.serverMediatedAi === 'true' ? containerEl.find('.chat-alternative-response').attr('data-translated-text') : containerEl.next().find('.chat-message-content').data('alternative-response');

        await that.handleAlternativeResponse(containerEl, text);
      }
    });

    $('body').on('click', '#js-chat-options-btn, #js-chat-options-btn-recording', function(e) {
      e.preventDefault();

      that.chatOptions.openModal();
    });

    $('body').on('click', '.js-user-chat-listen-yourself-btn', function(e) {
      e.preventDefault();

      if (e.handled !== true) {
        e.handled = true;

        const listenBtn = e.target.closest('.js-user-chat-listen-yourself-btn');
        if (listenBtn) {
          that.handleListenYourself(listenBtn);
        }
      }
    });
    if (this.pronunciationCharacters !== undefined) {
      document.addEventListener('readingAidCharactersChange',async (event) => {
        that.pronunciationCharacters = normalizePronunciationCharacters(event.detail);
        that.isReadingAidsOn = that.pronunciationCharacters !== 'disabled';
        that.destroyReadingAids();
        that.setupInitalLoadMessageTextRendering();
        if (that.guidedMode && that.guidedModeService) {
          that.guidedModeService.updateGuidedModeOverlay();
        }
      });
    }
    document.addEventListener('guidedModeToggled', (e) => {
      if (this.chatForm?.dataset.serverMediatedAi === 'true') return;
      if (e.detail.enabled) {
        // Turned ON: initialize if needed, then show suggestion for latest message
        if (!this.guidedModeService) {
          this.guidedModeService = new GuidedModeService(
            this.chatId,
            this.language,
            this.chatOptions.voice,
            this.chatOptions.voiceProvider,
            this.chatOptions.targetLanguage,
            this.popoverService,
            element => this.applyTransliteration(element)
          );
        }
        this.guidedMode = true;
        // Always generate a guided reply when enabled
        this.checkForGuidedModeReply();
      } else {
        // Turned OFF: hide overlay
        this.guidedMode = false;
        if (this.guidedModeService) {
          this.guidedModeService.hide();
        }
      }
    });

    document.addEventListener('highlightModeChanged', (e) => {
      // Update the highlighting mode in chatOptions and audio player service
      this.chatOptions.highlightMode = e.detail.mode;
      if (this.audioPlayerService) {
        this.audioPlayerService.setHighlightingMode(e.detail.mode);
      }
      if (this.chatMessageService) {
        this.chatMessageService.setHighlightingMode(e.detail.mode);
      }
    });
  }

  handleTranslation(chatMessageContentEl, data) {
    const translationBtn = chatMessageContentEl.querySelector('.js-user-chat-translation-btn');
    const aiTranslationBtn = chatMessageContentEl.querySelector('.js-chat-translation-btn');

    // add to closest chat-message-content
    if (data['error']) {
      console.log(data['error']);
      chatMessageContentEl.innerHTML += `<div class="alert alert-danger" role="alert">${data['error']}</div>`;
      if (translationBtn) { translationBtn.classList.remove('pulse'); };
      if (aiTranslationBtn) { aiTranslationBtn.classList.remove('pulse'); };
      $(this).prop('disabled', false);
    } else {

      if (chatMessageContentEl.querySelector('.chat-message-detail')) {
        if (data.data) {
          chatMessageContentEl.querySelector('.chat-message-detail').insertAdjacentHTML('afterend', data.data);
        }
      } else if (data.data) {
        chatMessageContentEl.innerHTML += data.data;
      }

      this.toggleChallengeClass(chatMessageContentEl.querySelector('.chat-translation'));

      if (translationBtn) { translationBtn.remove();}
      if (aiTranslationBtn) { aiTranslationBtn.remove();}
    }
  }

  // Function to handle chat correction
  async handleChatCorrection(containerEl, data) {
    if (this.chatForm?.dataset.serverMediatedAi === 'true') {
      const button = containerEl.find('.js-chat-correction-btn');
      const controller = new AbortController();
      const abort = () => controller.abort();
      let timedOut = false;
      const timer = setTimeout(() => { timedOut = true; abort(); }, 75000);
      window.addEventListener('pagehide', abort, { once: true });
      document.addEventListener('turbolinks:before-cache', abort, { once: true });
      document.addEventListener('turbo:before-cache', abort, { once: true });
      try {
        const correction = await requestCorrection(this.chatForm.dataset.contentId,
          containerEl.find('.chat-message-content').data('chat-message-id'),
          document.querySelector('meta[name="csrf-token"]')?.content, { signal: controller.signal });
        if (controller.signal.aborted || !containerEl[0]?.isConnected) return;
        await this.showCorrection(containerEl, correction);
        button.remove();
      } catch (_) {
        if ((controller.signal.aborted && !timedOut) || !containerEl[0]?.isConnected) return;
        button.prop('disabled', false);
        recoverWorkerError(this, 'Written correction could not be completed. Please try again.');
      } finally {
        clearTimeout(timer);
        button.prop('disabled', false);
        window.removeEventListener('pagehide', abort);
        document.removeEventListener('turbolinks:before-cache', abort);
        document.removeEventListener('turbo:before-cache', abort);
      }
      return;
    }
    if (data && data['text']) {
      await this.showCorrection(containerEl, data['text']);
      if (data['debug'] && data['prompt']) {
        this.showDebugData(containerEl, data);
      }

      const correctionBtn = containerEl.find('.js-chat-correction-btn');
      if (correctionBtn) {
        correctionBtn.remove();
      }
    } else {
      const text = containerEl.find('.js-chat-correction-btn').data('text');
      const language = this.chatOptions.language;
      const context = containerEl.prev().find('.chat-message-content').data('translated-text');
      this.correctTextHandler(containerEl, text, language, context);
    }
  }

  async showCorrection(containerEl, correctionText) {
    if (this.chatForm?.dataset.serverMediatedAi === 'true') {
      // Only the existing correction markup is allowed; model text stays text.
      const safe = document.createElement('div');
      safe.appendChild(correctionFragment(correctionText));
      correctionText = safe.innerHTML;
      containerEl.find('.chat-corrections').remove();
    }
    let translatableCorrectionText;
    const seeExplanationLink = `<br><button type="button" class="btn btn-link js-chat-correction-explanation-btn px-0">${I18n.t('javascript.chat.check_explain')}</button>`;
    const flagMarkup = this.flagCorrectionMarkup();
    if (correctionText == 'Perfect response.' || correctionText == I18n.t('javascript.chat.perfect_response')) {
      containerEl.find('.chat-message-content').append(`<div class="chat-corrections mt-4">${correctionText} ${seeExplanationLink}${flagMarkup}</div>`);
    } else {
      // Convert markdown-style double asterisks to HTML bold tags
      correctionText = correctionText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');

      let escapedCorrectionText = correctionText.replace(/"/g, '\\"').replace(/'/g, '&#39;');
      let correctionTextLines = correctionText.split("\n");
      
      // process correctionTextLines with readingAids. 
      // each line should be be processed individually
      if (this.shouldProcessText) {
        let translatableCorrectionProcessedText = await Promise.all(
          correctionTextLines.map(async (correctionTextLine) => {
            const processedCorrectionText = await this.processMessageText(correctionTextLine, this.readingAidService);
            return processedCorrectionText;
          })
        );

        // create span elements for each word in the readingAidCorrectionResponse
        translatableCorrectionText = translatableCorrectionProcessedText.map( (arrayOfCorrectionLines) => {
          return arrayOfCorrectionLines.map( ({html, text}) => {
            if (html === '<b>' || html === '</b>') {
              return html;
            }
            return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-phrase="${this.chatForm?.dataset.serverMediatedAi === 'true' ? String(text).replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll('<', '&lt;').replaceAll('>', '&gt;') : text}" data-language="${this.chatOptions.language}">${html}</span>`;
          }).join("");
        }).join("<br/>");

      } else {
        
        translatableCorrectionText = correctionTextLines.map(line => {
          return line.replace(/(<del>.*?<\/del>|<b>.*?<\/b>|\S+)/g, match => {
            return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-translated-text='${escapedCorrectionText}' data-language="${this.chatOptions.language}">${match}</span>`;
          });
        }).join("<br/>");
      }


      containerEl.find('.chat-message-content').append(`<div class="chat-corrections mt-4" data-translated-text='${escapedCorrectionText}'>${translatableCorrectionText} ${seeExplanationLink}${flagMarkup}</div>`);
      await this.applyTransliteration(containerEl.find('.chat-corrections').last().get(0));
    }
  }

  flagCorrectionMarkup() {
    if (!this.showCorrectionFlag) return '';
    const flagLabel = I18n.t('javascript.chat.flag_correction', { defaultValue: 'Flag as wrong' });
    const promptCopy = I18n.t('javascript.chat.flag_correction_prompt', {
      defaultValue: 'Think the AI is wrong? Flag it to help us improve. We recommend checking the explanation before confirming.'
    });
    const flagBtnLabel = I18n.t('javascript.chat.flag_correction_confirm', { defaultValue: 'Flag' });
    const cancelBtnLabel = I18n.t('javascript.chat.flag_correction_cancel', { defaultValue: 'Cancel' });
    const thankYouLabel = I18n.t('javascript.chat.flag_correction_thanks', { defaultValue: 'Thank you' });

    return `
      <button type="button" class="btn btn-link js-chat-correction-flag-btn chat-correction-flag-btn px-0 ms-2" aria-label="${flagLabel}" title="${flagLabel}">
        <i class="fa fa-flag" aria-hidden="true"></i>
      </button>
      <div class="js-chat-correction-flag-panel chat-correction-flag-panel d-none" role="region" aria-label="${flagLabel}">
        <div class="chat-correction-flag-text">${promptCopy}</div>
        <div class="chat-correction-flag-actions">
          <button type="button" class="btn btn-warning btn-sm js-chat-correction-flag-submit">${flagBtnLabel}</button>
          <button type="button" class="btn btn-link btn-sm js-chat-correction-flag-cancel">${cancelBtnLabel}</button>
        </div>
      </div>
      <div class="js-chat-correction-flag-toast chat-correction-flag-toast" aria-live="polite">${thankYouLabel}</div>
    `;
  }

  showDebugData(containerEl, debugData) {
    const generationParams = debugData.generation_params
      ? JSON.stringify(debugData.generation_params, null, 2)
      : `${debugData.client_provider} ${debugData.model}`;
    const debugHtml = `
      <div class="chat-debug mt-3">
        <details>
          <summary>Debug Information</summary>
          <div style="background-color: #f5f5f5; padding: 10px; border-radius: 5px;">
            <pre style="white-space: pre-wrap; background-color: #fffff; padding: 10px; border-radius: 5px;">${this.escapeHtml(generationParams)}</pre>
            <pre style="white-space: pre-wrap; background-color: #fffff; padding: 10px; border-radius: 5px;">${this.escapeHtml(debugData.prompt)}</pre>
          </div>
        </details>
      </div>
    `;
    containerEl.find('.chat-corrections').after(debugHtml);
  }

  escapeHtml(unsafe) {
    if (typeof unsafe !== 'string') {
      return '';
    }
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  // Function to handle chat correction
  handleCorrectionExplanation(containerEl, correctionExplanationText) {
    if (correctionExplanationText && this.chatForm?.dataset.serverMediatedAi === 'true') {
      containerEl.find('.chat-correction-explanations').remove();
      const output = document.createElement('div'); output.className = 'chat-correction-explanations mt-3'; output.style.whiteSpace = 'pre-wrap'; output.textContent = correctionExplanationText;
      containerEl.find('.chat-message-content').append(output);
      containerEl.find('.js-chat-correction-explanation-btn').remove();
      return;
    }
    if (correctionExplanationText) {
      // Convert markdown-style asterisks to HTML bold tags
      correctionExplanationText = correctionExplanationText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
      correctionExplanationText = correctionExplanationText.replace(/\*([^*]+)\*/g, '<b>$1</b>');

      let escapedCorrectionText = correctionExplanationText.replace(/"/g, '\\"').replace(/'/g, '&#39;');
      let correctionTextLines = correctionExplanationText.split("\n");
      let translatableCorrectionText = correctionTextLines.map(line => {
        return line.replace(/(<del>.*?<\/del>|<b>.*?<\/b>|\S+)/g, match => {
          return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-language="${this.chatOptions.language}">${match}</span>`;
        });
      }).join("<br>");
      containerEl.find('.chat-corrections').after(`<div class="chat-correction-explanations mb-0 pb-4">${translatableCorrectionText}</div>`);
      this.applyTransliteration(containerEl.find('.chat-correction-explanations').last().get(0));
      const correctionExplanationBtn = containerEl.find('.js-chat-correction-explanation-btn');
      if (correctionExplanationBtn) {
        correctionExplanationBtn.remove();
      }

    } else {
      const correctionText = containerEl.find('.chat-corrections').data('translated-text');
      const responseText = containerEl.find('.chat-message-content').data('translated-text')
      const messageText = containerEl.prev().find('.chat-message-content').data('translated-text');
      const language = this.chatOptions.language;
      this.explanationHandler(containerEl, correctionText, messageText, responseText, language);
    }
  }

  async handleAlternativeResponse(containerEl, alternativeResponseText) {
    const rawAlternativeResponseText = alternativeResponseText;
    const escapeAttribute = value => String(value).replaceAll('&', '&amp;').replaceAll('"', '&quot;').replaceAll("'", '&#39;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    if (alternativeResponseText && this.chatForm?.dataset.serverMediatedAi === 'true') {
      containerEl.find('.chat-alternative-response').remove();
      const safe = document.createElement('div'); safe.appendChild(correctionFragment(alternativeResponseText));
      alternativeResponseText = safe.innerHTML;
    }
    if (alternativeResponseText) {
      // Convert markdown-style asterisks to HTML bold tags
      alternativeResponseText = alternativeResponseText.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
      alternativeResponseText = alternativeResponseText.replace(/\*([^*]+)\*/g, '<b>$1</b>');

      let escapedAlternativeResponseText = alternativeResponseText.replace(/"/g, '\\"').replace(/'/g, '&#39;');
      let translatableAlternativeText = alternativeResponseText.replace(/(<del>.*?<\/del>|<b>.*?<\/b>|\S+)/g, match => {
        return `<span class="js-translatable-text js-audio-word word" style="cursor: pointer;" data-translated-text='${escapedAlternativeResponseText}' data-language="${this.chatOptions.language}">${match}</span>`;
      });
      
      
      let newAlternativeReponseElement = '';

      if (this.shouldProcessText) { 
        // create a new message set of elements as a string. 
        // for japanese, this will yield a mix of ruby elements and text.
        // for chinese, this will yield a mix of text elements.
        // for other languages, this will yield a mix of text elements.
        const readingAidAlternativeResponse = await this.processMessageText(alternativeResponseText, this.readingAidService);
        // create span elements for each word in the readingAidAlternativeResponse
        let readingAidAlternativeResponseSpans = '';
        
        if (typeof readingAidAlternativeResponse === 'string') {
          readingAidAlternativeResponseSpans = `<span class="js-translatable-text js-audio-word word" data-translated-text='${escapedAlternativeResponseText}' data-language="${this.chatOptions.language}" data-phrase="${this.chatForm?.dataset.serverMediatedAi === 'true' ? escapeAttribute(rawAlternativeResponseText) : alternativeResponseText}">${readingAidAlternativeResponse}</span>`;
        } else {
          readingAidAlternativeResponseSpans = readingAidAlternativeResponse.map(({html, text}) => {  
            return `<span class="js-translatable-text js-audio-word word" data-translated-text='${this.chatForm?.dataset.serverMediatedAi === 'true' ? escapeAttribute(text) : text}' data-language="${this.chatOptions.language}" data-phrase="${this.chatForm?.dataset.serverMediatedAi === 'true' ? escapeAttribute(text) : text}">${html}</span>`;
          }).join('');
        }

        newAlternativeReponseElement = this.createAlternativeResponseElement(this.chatForm?.dataset.serverMediatedAi === 'true' ? rawAlternativeResponseText : escapedAlternativeResponseText, this.chatOptions.language, readingAidAlternativeResponseSpans, containerEl.attr('data-chat-message-id'));
        
      } else {
        // add the translatable alternative response
        newAlternativeReponseElement = this.createAlternativeResponseElement(this.chatForm?.dataset.serverMediatedAi === 'true' ? rawAlternativeResponseText : escapedAlternativeResponseText, this.chatOptions.language, translatableAlternativeText, containerEl.attr('data-chat-message-id'));
      }
      containerEl.find('.chat-message-content').append(newAlternativeReponseElement);
      await this.applyTransliteration(containerEl.find('.chat-alternative-response').last().get(0));


      // remove the button after the alternative response is added
      const alternativeResponseBtn = containerEl.find('.js-alternative-response-btn');
      if (alternativeResponseBtn) {
        alternativeResponseBtn.remove();
      }
    } else {
      const responseText = containerEl.find('.chat-message-content').data('translated-text')
      const language = this.chatOptions.language;
      const context = containerEl.prev().find('.chat-message-content').data('translated-text');

      this.alternativeResponseHandler(containerEl, responseText, language, context);
    }
  }

  createAlternativeResponseElement(originalText, language, responseContent, messageId = null) {
    const serverMediated = this.chatForm?.dataset.serverMediatedAi === 'true';
    if (serverMediated) {
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(messageId || '')) throw new Error('Invalid owned alternative');
      originalText = originalText.replaceAll('&', '&amp;').replaceAll('\"', '&quot;').replaceAll("'", '&#39;').replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    }
    const audio = serverMediated ? `js-cartus-message-speech" data-chat-message-id="${messageId}" data-speech-selection="alternative` : 'js-play-chat-audio-btn';
    return `<div class="chat-alternative-response mt-4" data-translated-text="${originalText}">
        <span class="font-weight-bold">Alternative:</span>
        ${responseContent}  
        ${!serverMediated || this.chatForm.dataset.messageSpeechEnabled === 'true' ? `<button type="button" class="btn btn-listen-alternative btn-sm ml-2 ${audio}" data-text="${originalText}" data-language="${language}">
          <i class="fa fa-volume-up" aria-hidden="true"></i>
        </button>` : ''}
        ${!serverMediated ? `<button type="button" class="btn btn-say-it btn-sm ml-1 js-say-it-btn">
          <i class="fa fa-microphone"></i> Say it
        </button>` : ''}
      </div>`;
  }

  async showAutomaticSuggestions(node, suggestions, { isCurrent = () => true } = {}) {
    const current = () => isCurrent() && this.chatForm.dataset.automaticSuggestions === 'true' && node.isConnected &&
      node === [...document.querySelectorAll('.chat-content .chat-message:not(.self)[data-chat-message-id]')].at(-1);
    if (!current()) return;
    // Match consumer guided mode's delay, overlay, word interactions and icons.
    await new Promise(resolve => setTimeout(resolve, 6000));
    if (!current()) return;
    if (!this.guidedModeService) this.guidedModeService = new GuidedModeService(this.chatId, this.language,
      this.chatOptions.voice, this.chatOptions.voiceProvider, this.chatOptions.targetLanguage, this.popoverService);
    this.guidedModeService.translationService = this.translationService;
    this.guidedModeService.setReadingAidService(this.readingAidService, this.isReadingAidsOn);
    const id = node.dataset.chatMessageId;
    await this.guidedModeService.show(suggestions[0], '', { messageId: id });
    if (!current()) { this.guidedModeService.hide(); return; }
    await this.translationService.translateSentence(suggestions[0], data => {
      if (!current() || this.guidedModeService.audioBtn.dataset.chatMessageId !== id) return;
      this.guidedModeService.translationEl.textContent = data.error || data.translation || '';
    });
  }

  async handleSuggestedReply(suggestionData = null, messageId = null, isCurrent = () => true) {
    if (this.chatForm?.dataset.serverMediatedAi === 'true' && !suggestionData) return showTenantSuggestions(this);

    const lastChatMessage = $('.chat-content .chat-message:not(.self):last');
    const generation = this.tenantUiGeneration || 0;
    const ownedMessageId = messageId || lastChatMessage.data('chat-message-id');
    const stillCurrent = () => isCurrent() && this.chatForm.isConnected && (this.tenantUiGeneration || 0) === generation &&
      $('.chat-content .chat-message:not(.self):last').data('chat-message-id') === ownedMessageId;

    if (suggestionData != null && suggestionData != undefined) {
      this.suggestionBtn.classList.remove('pulse');
      this.suggestionBtn.disabled = false;
      // we should have a this.readingAidService that is used for the suggested reply. 
      let readingAidService = null;
      if (this.chatOptions.language === 'japanese') readingAidService = this.japaneseTextService;
      if (this.chatOptions.language === 'chinese') readingAidService = this.chineseTextService;
      if (this.chatOptions.language === 'chinese' && !readingAidService) {
        readingAidService = await this.ensureChineseTextService();
      }
      if (this.chatForm?.dataset.serverMediatedAi === 'true') {
        await this.suggestedReplyService.showSuggestions(this, suggestionData['suggestions'], this.chatOptions.language, readingAidService, this.isReadingAidsOn, { messageId: ownedMessageId, speechEnabled: this.chatForm.dataset.messageSpeechEnabled === 'true', pronunciationEnabled: this.chatForm.dataset.pronunciationEnabled === 'true', chatId: this.chatId, isCurrent: stillCurrent });
      } else {
        this.suggestedReplyService.showSuggestions(this, suggestionData['suggestions'], this.chatOptions.language, readingAidService, this.isReadingAidsOn);
      }
    } else {
      const responseText = lastChatMessage.find('.chat-message-content').data('translated-text')
      const language = this.chatOptions.language;
      const context = lastChatMessage.prev().find('.chat-message-content').data('translated-text');
      this.suggestionBtn.disabled = true;
      this.suggestionBtn.classList.add('pulse');

      this.suggestedReplyHandler(lastChatMessage, responseText, language, context);
    }
  }

  // The turtle slows the reply's own audio in the browser, as the app does: it
  // plays at once, is slowed only once, at full quality, and the reply's word
  // timings still match it. The server's slowed copy is only for a reply with
  // no audio here yet (LAN-1177).
  async handleSlowAudio(slowAudioEl, audioUrl, slowAudioUrl = null) {
    const playAudioEl = slowAudioEl.closest('.chat-message-audio')?.querySelector('.js-play-chat-audio-btn');
    const usesPlaybackRate = !!parseFloat(playAudioEl?.dataset.playbackRate);
    if (!usesPlaybackRate) {
      // Flash keeps master's baked-in speed and existing server-slowed copy.
      if (!slowAudioUrl) {
        this.slowAudioService.fetch('Chat', this.chatId, audioUrl || null, slowAudioEl.dataset.chatMessageId);
      } else {
        this.playSlowAudio(slowAudioEl, slowAudioUrl, SLOW_REPLAY_RATE);
      }
      return;
    }
    const replyAudioUrl = await this.slowReplyAudioUrl(slowAudioEl, audioUrl);

    if (replyAudioUrl) {
      this.playSlowAudio(slowAudioEl, replyAudioUrl, SLOW_REPLAY_RATE);
    } else if (slowAudioUrl === undefined || slowAudioUrl === null || slowAudioUrl === '') {
      // Fetch slow audio from server; with no reply audio it is retrieved via chat_message_id
      this.slowAudioService.fetch('Chat', this.chatId, null, slowAudioEl.dataset.chatMessageId);
    } else {
      // The server's copy is already stretched to SLOW_AUDIO_SERVER_SPEED: play
      // the rest of the way to the same speed, with the timings stretched to match.
      this.playSlowAudio(slowAudioEl, slowAudioUrl, SLOW_REPLAY_RATE / SLOW_AUDIO_SERVER_SPEED, 1 / SLOW_AUDIO_SERVER_SPEED);
    }
  }

  // The reply's audio: its saved URL, else the copy streamed into this page.
  // A stored blob URL from an earlier page load no longer resolves.
  async slowReplyAudioUrl(slowAudioEl, audioUrl) {
    if (audioUrl) return audioUrl;

    const chatMessageId = slowAudioEl.dataset.chatMessageId;
    const blobUrl = chatMessageId ? audioStorageService.getAudioUrl(chatMessageId) : null;
    if (!blobUrl) return null;

    try {
      return (await fetch(blobUrl)).ok ? blobUrl : null;
    } catch (error) {
      return null;
    }
  }

  playSlowAudio(slowAudioEl, url, playbackRate, timingScale = 1) {
    const text = slowAudioEl.dataset.text;
    const wordEl  = $(slowAudioEl).closest('.word-container')[0];

    slowAudioEl.classList.remove('loading', 'pulse');

    // Update the button's audioUrl dataset so it can be replayed
    slowAudioEl.dataset.audioUrl = url;

    this.playAudioHandler(wordEl, slowAudioEl, text, this.language, playbackRate, timingScale);
  }

  // Method to play audio
  playAudioHandler(wordEl, soundEl, text, language, playbackRate = 1.0, timingScale = 1) {
    this.audioPlayerService.setupAudio(wordEl, soundEl, text, language, { voice: this.chatOptions.voice, voice_provider: this.chatOptions.voiceProvider, speed: this.chatOptions.voiceSpeed }, true, playbackRate, timingScale)
  }

  correctTextHandler(element, text, language, context = null) {
    const that = this;
    const correctionBtn = element.find('.js-chat-correction-btn');
    const chatMessageId = element.data('chat-message-id');
    correctionBtn.addClass('pulse');

    that.textCorrectionService.fetchCorrection(
      that.chatId,
      chatMessageId,
      text,
      language,
      context,
      that.chatOptions.clientProvider,
      that.chatOptions.model,
      that.debug
    )
      .catch((error) => {
        correctionBtn.remove();
      });
  }

  explanationHandler(element, correctionText, messageText, responseText, language) {
    const that = this;
    const explanationBtn = element.find('.js-chat-correction-explanation-btn');
    const chatMessageId = element.data('chat-message-id');
    explanationBtn.addClass('pulse');

    that.correctionExplanationService.fetchExplanation(
      that.chatId,
      chatMessageId,
      correctionText,
      messageText,
      responseText,
      language,
      that.chatOptions.clientProvider,
      that.chatOptions.model,
    )
      .catch((error) => {
        explanationBtn.remove();
      });
  }

  alternativeResponseHandler(element, text, language, context = null) {
    const that = this;
    const alternativeResponseBtn = element.find('.js-alternative-response-btn');
    const chatMessageId = element.data('chat-message-id');
    alternativeResponseBtn.addClass('pulse');

    that.alternativeResponseService.fetch(
      that.chatId,
      chatMessageId,
      text,
      language,
      context,
      that.chatOptions.clientProvider,
      that.chatOptions.model
    )
      .catch((error) => {
        alternativeResponseBtn.remove();
      });
  }

  suggestedReplyHandler(element, text, language, context = null) {
    const that = this;
    const chatMessageId = element.data('chat-message-id');

    that.suggestedReplyService.fetch(that.chatId, chatMessageId, text, language, context) 
      .catch((error) => {
        suggestionBtn.disabled = false;
      });
  }

  toggleChallengeClass(element) {
    const $element = $(element);

    // Special handling for chat translations
    if ($element.hasClass('chat-translation') || $element.closest('.chat-translation').length > 0) {
      const $translationContainer = $element.hasClass('chat-translation') ?
        $element :
        $element.closest('.chat-translation');

      // Toggle challenge class on container and all its word spans
      $translationContainer.toggleClass('chat-challenge');
      $translationContainer.find('.chat-translation.word').toggleClass('challenge');
      return;
    }

    // Regular handling for non-translation elements.
    // Target every hideable word span rather than only the translatable ones. Reading
    // aid messages (Japanese/Chinese) re-render the line into segments, and only the
    // segments with a translation keep `js-translatable-text`: punctuation loses it and
    // non-target-language words are rewritten to `js-translatable-text-no-translation`.
    // Selecting on `js-translatable-text` therefore left those segments stuck behind the
    // grey block while the target-language text revealed on its own.
    const $wordSpans = $element.find(HIDEABLE_WORD_SELECTOR);

    // If there are no word spans, check if the element itself is one
    if ($wordSpans.length === 0 && $element.is(HIDEABLE_WORD_SELECTOR)) {
      $element.toggleClass('challenge');
      return;
    }

    // Drive every span to the same state instead of flipping each one independently, so
    // a partially revealed message (reading aid text shown, punctuation/English still
    // hidden) collapses back to "all hidden" or "all shown" rather than inverting.
    const shouldHide = shouldHideAll($wordSpans.map((_, el) => $(el).hasClass('challenge')).get());
    $wordSpans.toggleClass('challenge', shouldHide);

    // Handle wrapper elements (like <b> tags)
    $wordSpans.each(function() {
      let $parent = $(this).parent();
      // Only handle direct parent that's not a layout container
      if ($parent.length && !$parent.is(LAYOUT_CONTAINER_SELECTOR)) {
        // If all children have challenge class, add it to parent
        // If no children have challenge class, remove it from parent
        const allChildrenHaveChallenge = $parent.children(`${HIDEABLE_WORD_SELECTOR}.challenge`).length ===
                                       $parent.children(HIDEABLE_WORD_SELECTOR).length;

        if (allChildrenHaveChallenge) {
          $parent.addClass('challenge');
        } else {
          $parent.removeClass('challenge');
        }
      }
    });
  }

  sendMessage(message, event) {
    this.chatInput.value = message;
    this.handleSubmitForm(this, event);
  }

  removeStartChatContainer() {
    this.chatElementContainer.classList.remove('chat-content-w-centered');
    this.chatElementContainer.classList.add('chat-content-w-default');
    this.chatElement.classList.remove('chat-content-centered');
    this.chatElement.classList.add('chat-content-default');

    if (this.chatElement.querySelector('.js-start-chat-container')) {
      this.chatElement.querySelector('.js-start-chat-container').remove();
    }

  }

  startChat() {
    this.chatInput.value = "Hi";
    this.handleSubmitForm(this, null);
  }

  waitForWorkerAndStartChat() {
    // Check if Worker is ready, if not wait and retry
    const checkInterval = setInterval(() => {
      if (this.chatConnection && this.chatConnection.isWorkerReady()) {
        clearInterval(checkInterval);
        this.startChat();
      }
    }, 100); // Check every 100ms

    // Timeout after 5 seconds and fall back to ActionCable
    setTimeout(() => {
      clearInterval(checkInterval);
      // chatConnection is usually present by now, but can still be null if
      // connection setup/re-initialization races with the auto-start timeout.
      // Treat that the same as worker-not-ready and use the Rails fallback.
      if (!this.chatConnection || !this.chatConnection.isWorkerReady()) {
        // Worker not ready after 5 seconds, using ActionCable
        this.startChat();
      }
    }, 5000);
  }

  setTopic(topicCategory, chatSubtopicCategory, topic, event) {
    this.chatTopicCategory.value = topicCategory;
    this.chatSubtopicCategory.value = chatSubtopicCategory;
    this.chatTopic.value = topic;
    this.chatInput.value = "Hi";
    this.handleSubmitForm(this, event || null);
  }

  /**
   * Handles chat form submission via event or programmatically
   * @param {ChatService} that - The ChatService instance
   * @param {Event|null} event - The submit event, or null for programmatic submission
   */
  handleSubmitForm(that, event) {
    // A Turbolinks visit (End Chat, the feedback modal's link, browser back)
    // keeps this instance and its document-level click handlers alive after
    // its chat page is gone. Topic-menu clicks on the next page then reached
    // setTopic() here and sent "Hi" to this old chat (LAN-1238), so a page
    // that is no longer in the document never submits.
    if (that.chatElement?.isConnected === false) return;
    if (that.chatInput.value.length > 0) {
      if (that.chatForm?.dataset.serverMediatedAi === 'true') that.tenantChatAutomation?.submitted();
      // Store the last message
      that.lastMessage = that.chatInput.value;
      
      // Generate a temporary message ID for tracking
      const tempMessageId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      // Track message submission
      chatMetricsClient.trackMessageSubmitted(that.chatId, tempMessageId);
      
      // Add temp message ID to form as hidden field
      let tempMessageIdField = that.chatForm.querySelector('input[name="temp_message_id"]');
      if (!tempMessageIdField) {
        tempMessageIdField = document.createElement('input');
        tempMessageIdField.type = 'hidden';
        tempMessageIdField.name = 'temp_message_id';
        that.chatForm.appendChild(tempMessageIdField);
      }
      tempMessageIdField.value = tempMessageId;

      // Hide guided mode overlay if it exists
      if (that.guidedMode && that.guidedModeService) {
        that.guidedModeService.hide(true);
      }

      const lastChatMessageId = $('.chat-message').last().data('chat-message-id');
      const nextChatMessageId = lastChatMessageId === undefined ? 0 : parseInt(lastChatMessageId) + 1;
      let userAvatarEl = $('.chat-message-avatar.self').last().html();

      if (lastChatMessageId === undefined) {
        that.chatTopicService.hideChatTopics();
        that.chatTopicService.hideTopicMenu();
      }

      if (userAvatarEl === undefined) {
        userAvatarEl = `<img title="${that.chatForm.dataset.avatarTitle}" src="${that.chatForm.dataset.userAvatarPath}">`;
      }

      that.chatSubmitBtn.disabled = true;

      if (nextChatMessageId === 0) {
        if (that.chatTopicCategory.value === 'roleplay') {
          try {
            const selectedTopic = that.chatTopicService.topics.find((topic) => {
              return topic.key === that.chatTopic.value;
            }).label;

            if (selectedTopic) {
              const loadingMessage = `<strong>${I18n.t('javascript.chat.roleplay_loading_with_topic', { topic: selectedTopic.toLowerCase() })}</strong>.`;
              that.addChatLoader(that.chatElement, loadingMessage);
            } else {
              const loadingMessage = I18n.t('javascript.chat.roleplay_loading');
              that.addChatLoader(that.chatElement, loadingMessage);
            }
          } catch (error) {
            const loadingMessage = `Your role play will load in 5-8 seconds.`;
            that.addChatLoader(that.chatElement, loadingMessage);
          }
        } else {
          const languageLabel = this.chatOptions.languageLabel || (this.language.charAt(0).toUpperCase() + this.language.slice(1));
          const loadingMessage = I18n.t('javascript.chat.conversation_starting', { language: languageLabel });
          that.addChatLoader(that.chatElement, loadingMessage);
        }

        if (this.startChatBtn) {
          this.removeStartChatContainer();
        }

      } else {
        that.addChatLoader(that.chatElement);
      }

      that.scrollToBottom();

      // Check if we're using Worker backend and it's ready
      const useWorker = that.chatConnection && that.chatConnection.isWorkerReady();

      if (useWorker) {
        // Send message directly to Worker in expected format
        const messageData = {
          type: "message",                    // Add message type
          content: that.lastMessage,          // Change 'text' to 'content'
          role: "user",                        // Add role
          temp_id: tempMessageId,              // Change to snake_case
          
          // Additional context (Worker can use these)
          language: that.language,
          targetLanguage: that.targetLanguage,
          mode: that.mode,
          topicCategory: that.chatTopicCategory.value,
          subtopicCategory: that.chatSubtopicCategory.value,
          topic: that.chatTopic.value
        };
        
        that.chatConnection.sendMessage(messageData);
      } else {
        if (event) {
          // Use traditional Rails form submission
          Rails.handleRemote.call(that.chatForm, event);
        } else {
          // Programmatically submit the form without re-entering this submit
          // handler. Rails UJS still receives the bubbling submit event and
          // sends the remote form request with the temp_message_id above.
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          submitEvent.chatServiceAlreadyProcessed = true;
          that.chatForm.dispatchEvent(submitEvent);
        }
      }

      that.chatInput.value = '';
      that.keyboardToggleVisible = that.loadKeyboardPreference();
      that.chatInput.dispatchEvent(new Event('input', { bubbles: true }));
      that.updateInputBarState();
    }
  }

  setupInputBarState() {
    if (!this.chatInput || !this.inputShell) {
      return;
    }

    this.bindPauseBtnListener();

    this.updateInputBarState();
  }

  bindPauseBtnListener() {
    if (!this.pauseBtn) return;
    if (this._pauseBtnHandler) {
      this.pauseBtn.removeEventListener('click', this._pauseBtnHandler);
    }
    this._pauseBtnHandler = (event) => {
      if (this.isRecording) return;
      event.preventDefault();
      this.keyboardToggleVisible = !this.keyboardToggleVisible;
      this.persistKeyboardPreference(this.keyboardToggleVisible);
      this.updateInputBarState();
      if (this.keyboardToggleVisible) {
        setTimeout(() => this.chatInput.focus(), 0);
      } else {
        this.chatInput.blur();
      }
    };
    this.pauseBtn.addEventListener('click', this._pauseBtnHandler);
  }

  restoreChatSubmitState() {
    this.chatSubmitBtn.disabled = false;
  }

  updateInputBarState() {
    if (!this.chatInput || !this.inputShell) {
      return;
    }

    const hasText = this.chatInput.value.trim().length > 0;
    const recorder = this.chatOptions && this.chatOptions.audioRecordingService;
    const isRecording = !!(recorder && recorder.isRecording);
    // A recording, and the wait for its transcript, use the composer layout:
    // the box shows the live transcript and stays read-only.
    const live = recorder && recorder.liveTranscript;
    const liveActive = !!live;
    const liveRecording = liveActive && isRecording;
    const liveFinishing = liveActive && !isRecording;
    const showComposer = liveActive || (!isRecording && (this.keyboardToggleVisible || hasText));
    this.inputShell.dataset.liveTranscript = liveRecording ? 'recording' : (liveActive ? 'finishing' : 'off');

    this.inputShell.dataset.keyboardVisible = showComposer ? 'true' : 'false';
    this.inputShell.dataset.hasText = hasText ? 'true' : 'false';
    this.inputShell.dataset.recordingActive = isRecording ? 'true' : 'false';
    const primaryActions = this.inputShell.querySelector('.chat-input-primary-actions');
    const secondaryActions = this.inputShell.querySelector('.chat-input-secondary-actions');

    if (primaryActions) {
      primaryActions.classList.toggle('d-none', showComposer);
    }

    if (this.suggestionBtn) {
      this.suggestionBtn.dataset.recordingActive = isRecording ? 'true' : 'false';

      if (showComposer && secondaryActions && this.suggestionBtn.parentElement !== secondaryActions) {
        secondaryActions.insertBefore(this.suggestionBtn, secondaryActions.firstChild);
      } else if (!showComposer && primaryActions && this.suggestionBtn.parentElement !== primaryActions) {
        // Insert after the recording-view settings button (first child) to maintain order:
        // settings → lightbulb → record → keyboard → three-dots
        const settingsBtn = primaryActions.querySelector('#js-chat-options-btn-recording');
        if (settingsBtn) {
          primaryActions.insertBefore(this.suggestionBtn, settingsBtn.nextSibling);
        } else {
          primaryActions.insertBefore(this.suggestionBtn, primaryActions.firstChild);
        }
      }

      this.suggestionBtn.classList.toggle('btn-chat-input-pill-primary', !showComposer);
      this.suggestionBtn.classList.toggle('btn-chat-input-pill-secondary', showComposer);
      // Hide lightbulb when composer is open — not needed since user has started responding
      this.suggestionBtn.classList.toggle('d-none', showComposer);
    }

    if (this.liveTranscriptBox) {
      const useLiveBox = liveActive && !live.mirror;
      this.liveTranscriptBox.classList.toggle('d-none', !useLiveBox);
      this.chatInput.classList.toggle('d-none', useLiveBox);
      this.chatInput.readOnly = liveActive;
    }
    if (this.recordingIndicator) {
      this.recordingIndicator.classList.toggle('d-none', !liveRecording);
    }

    // While recording, pause (stop and review) and record (stop and send) sit
    // where the composer's send button is; afterwards pause goes home.
    if (this.pauseBtn) {
      if (liveRecording && this.inputComposer) {
        if (this.pauseBtn.parentElement !== this.inputComposer) this.inputComposer.insertBefore(this.pauseBtn, this.chatSubmitBtn);
      } else if (primaryActions && this.pauseBtn.parentElement !== primaryActions) {
        primaryActions.insertBefore(this.pauseBtn, this.pauseBtnHomeNext && this.pauseBtnHomeNext.parentElement === primaryActions ? this.pauseBtnHomeNext : null);
      }
      this.pauseBtn.classList.toggle('d-none', liveActive && !isRecording);
    }

    // Move record button to secondary actions when composer is open
    if (this.recordBtn && liveRecording && this.inputComposer) {
      if (this.recordBtn.parentElement !== this.inputComposer) this.inputComposer.insertBefore(this.recordBtn, this.chatSubmitBtn);
    } else if (this.recordBtn) {
      if (showComposer && secondaryActions && this.recordBtn.parentElement !== secondaryActions) {
        secondaryActions.insertBefore(this.recordBtn, secondaryActions.firstChild);
      } else if (!showComposer && primaryActions && this.recordBtn.parentElement !== primaryActions) {
        if (this.suggestionBtn && this.suggestionBtn.parentElement === primaryActions) {
          primaryActions.insertBefore(this.recordBtn, this.suggestionBtn.nextSibling);
        } else {
          primaryActions.appendChild(this.recordBtn);
        }
      }
    }

    if (this.inputComposer) {
      this.inputComposer.classList.toggle('d-none', !showComposer);
    }

    // Show clear/bin button only when composer is visible, has text, and not recording
    if (this.clearTextBtn) {
      // During a recording the bin discards it; while the transcript is on its way it is hidden.
      this.clearTextBtn.classList.toggle('d-none', !(liveRecording || (showComposer && hasText && !isRecording && !liveActive)));
      // Keep bin button right after record button in secondary actions
      if (showComposer && secondaryActions && this.recordBtn && this.recordBtn.parentElement === secondaryActions) {
        this.recordBtn.after(this.clearTextBtn);
      }
      // While recording the record button sits in the composer, so the bin
      // would stay wherever the last layout left it: before the timer when the
      // recording began from a draft, after it when it began from the voice
      // layout. Pin it first, before the timer, as the app does.
      if (liveRecording && this.recordingIndicator && this.recordingIndicator.parentElement) {
        this.recordingIndicator.before(this.clearTextBtn);
      }
    }

    // Disable send button when recording (record button below textbox handles submission)
    this.chatSubmitBtn.disabled = !hasText || isRecording || liveActive;
    // While the transcript is on its way, send waits with a spinner: the box
    // shows the live text, and sending that would beat the final result.
    this.chatSubmitBtn.classList.toggle('chat-send-pending', liveFinishing);
    this.chatSubmitBtn.setAttribute('aria-busy', liveFinishing ? 'true' : 'false');
    // While recording, the record button is the send button.
    this.chatSubmitBtn.classList.toggle('d-none', !showComposer || liveRecording);
    if (isRecording) {
      this.chatSubmitBtn.style.opacity = '0.3';
      this.chatSubmitBtn.style.pointerEvents = 'none';
    } else {
      this.chatSubmitBtn.style.opacity = '';
      this.chatSubmitBtn.style.pointerEvents = '';
    }

    if (this.recordBtn) {
      const recordIcon = this.recordBtn.querySelector('i');
      if (recordIcon && !isRecording) {
        // When composer is open, record button is in secondary row — always show mic
        // Send-when-text is handled by the composer send button
        recordIcon.className = 'fa fa-microphone';
      }
      // When in secondary row, match secondary button size
      this.recordBtn.classList.toggle('btn-chat-input-pill-secondary', showComposer && !isRecording);
      this.recordBtn.classList.toggle('btn-chat-input-pill-primary', !showComposer && !isRecording);
      this.recordBtn.classList.toggle('btn-chat-composer-send', liveRecording);
      this.recordBtn.classList.toggle('btn-chat-send', isRecording);
      this.recordBtn.classList.toggle('btn-chat-record', !isRecording);
    }

    if (this.pauseBtn && !isRecording) {
      const pauseIcon = this.pauseBtn.querySelector('i');
      if (pauseIcon) {
        pauseIcon.className = showComposer ? 'fa fa-keyboard-o chat-keyboard-off-icon' : 'fa fa-keyboard-o';
      }
    }
  }

  // Function to add the loader
  addChatLoader(targetElement, message = null) {
    const loader = document.createElement('div');
    loader.className = 'chat-loader mt-3';

    // display message if provided
    if (message) {
      loader.innerHTML = `
      <div class="chat-message loading">
        <div class="chat-message-content-w">
          <div class="chat-message-content">
            ${message}
          </div>
        </div>
      </div>
      `;

      for (let i = 0; i < 3; i++) {
        const dot = document.createElement('span');
        dot.className = 'chat-dot';
        loader.appendChild(dot);
      }

      this.isLoadingMessage = true;
    } else {
      for (let i = 0; i < 3; i++) {
        const dot = document.createElement('span');
        dot.className = 'chat-dot';
        loader.appendChild(dot);
      }
    }

    targetElement.appendChild(loader);

  }

  // Function to remove the loader
  removeChatLoader(targetElement) {
    const loader = targetElement.querySelector('.chat-loader');
    if (loader) {
      targetElement.removeChild(loader);
    }

    this.isLoadingMessage = false;
  }

  scrollToBottom() {
    // Scroll to last message only — removed redundant container.scroll() call
    // that was causing double smooth-scroll jitter on mobile
    const messages = document.querySelectorAll('.chat-message');
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      lastMessage.scrollIntoView({ behavior: 'smooth' });
    }
  }

  // Textarea auto-expand up to 45% of viewport height
  setupTextareaAutoExpand() {
    const textarea = this.chatInput;
    if (!textarea) return;

    // Disconnect any observer/listener left over from a previous instance so
    // we don't stack them across Turbolinks navigations.
    this.teardownTextareaAutoExpand();

    textarea.addEventListener('input', () => {
      textarea.style.height = 'auto';
      const maxHeight = window.innerHeight * 0.45;
      const newHeight = Math.min(textarea.scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;
    });

    // Keep --controls-height in sync with actual controls height (for fixed positioning)
    // Applies to: tablet/desktop (768px+ wide, 768px+ tall) OR short screens (<=767px tall, 768px+ wide)
    const tallScreenQuery = window.matchMedia('(min-width: 768px) and (min-height: 768px)');
    const shortScreenQuery = window.matchMedia('(max-height: 767px) and (min-width: 768px)');
    const needsFixedControls = tallScreenQuery.matches || shortScreenQuery.matches;
    const chatControls = document.querySelector('.chat-controls');

    if (needsFixedControls && chatControls && window.ResizeObserver) {
      // ResizeObserver catches viewport resizes AND content changes (e.g. growing textarea)
      this._controlsResizeObserver = new ResizeObserver(() => {
        document.documentElement.style.setProperty(
          '--controls-height',
          chatControls.getBoundingClientRect().height + 'px'
        );
      });
      this._controlsResizeObserver.observe(chatControls);
    } else if (needsFixedControls && chatControls) {
      // Fallback for older browsers
      this._updateControlsHeight = () => {
        document.documentElement.style.setProperty(
          '--controls-height',
          chatControls.getBoundingClientRect().height + 'px'
        );
      };
      this._updateControlsHeight();
      window.addEventListener('resize', this._updateControlsHeight);
    }
  }

  teardownTextareaAutoExpand() {
    if (this._controlsResizeObserver) {
      this._controlsResizeObserver.disconnect();
      this._controlsResizeObserver = null;
    }
    if (this._updateControlsHeight) {
      window.removeEventListener('resize', this._updateControlsHeight);
      this._updateControlsHeight = null;
    }
  }

  cleanSentence(sentence) {
    // Skip cleanup for RTL languages (Arabic, Hebrew, etc)
    if (/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u0590-\u05FF]/.test(sentence)) {
      return sentence.trim();
    }

    const cleanups = [
      // Replace multiple spaces with a single space
      [/\s+/g, ' '],

      // Handle punctuation spacing
      [/\s*([,.:;?!»)}\]°"'%‰‱])/g, '$1'],
      [/([«({\[])\s*/g, '$1'],
      [/(\S)\s*([:.;,])/g, '$1$2'],

      // Handle apostrophes
      [/(\S)\s*([''])\s*(\S)/g, '$1$2$3'],

      // Handle quotation marks
      [/"(\S)/g, '" $1'],
      [/(\S)"/g, '$1 "'],
      [/([.!?])\s*"/g, '$1"'],

      // Remove only leading punctuation and spaces while preserving non-Latin characters
      [/^\s*([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~\s]+)*/, '']
    ];

    return cleanups.reduce((text, [pattern, replacement]) =>
      text.replace(pattern, replacement), sentence).trim();
  }

  getSentenceOfWord(element) {
    const parent = element.closest('.chat-message-detail');
    if (!parent) return '';

    const translatedText = parent.dataset.translatedText;
    if (!translatedText) return '';

    // Split the translated text into sentences based on end-of-sentence punctuation followed by whitespace or end
    const endOfSentenceRegex = /([.!?。！？។៕။…\u3002\uFF01\uFF1F\u0964\u0965])\s*|$/g;
    let sentences = [];
    let start = 0;
    let match;

    while ((match = endOfSentenceRegex.exec(translatedText)) !== null) {
        const punctuation = match[1];
        if (punctuation) {
            const end = match.index + punctuation.length;
            const sentence = translatedText.slice(start, end).trim();
            if (sentence) sentences.push(sentence);
            start = end + (translatedText[end] === ' ' ? 1 : 0); // Skip the punctuation and any following space
        } else {
            // Handle remaining text at the end
            const sentence = translatedText.slice(start).trim();
            if (sentence) sentences.push(sentence);
            break;
        }
    }

    // Collect all js-translatable-text spans in the parent
    const spans = Array.from(parent.querySelectorAll('.js-translatable-text'));
    const clickedIndex = spans.indexOf(element);
    if (clickedIndex === -1) return '';

    // Split the translated text into words (same as spans' order)
    const translatedWords = translatedText.split(/\s+/).filter(word => word.length > 0);

    // Find which sentence contains the clicked word's index
    let currentWordIndex = 0;
    for (const sentence of sentences) {
        const sentenceWords = sentence.split(/\s+/).filter(word => word.length > 0);
        const sentenceLength = sentenceWords.length;
        const sentenceEnd = currentWordIndex + sentenceLength;

        if (clickedIndex >= currentWordIndex && clickedIndex < sentenceEnd) {
            return this.cleanSentence(sentence);
        }

        currentWordIndex = sentenceEnd;
    }

    return '';
}

  getAlternativeModel() {
    return this.chatOptions.aiModel === 'v1' ? 'V2' : 'V1';
  }

  getCurrentModel() {
    return this.chatOptions.aiModel === 'v1' ? 'V1' : 'V2';
  }

  switchModel() {
    let newAIModel, newProvider, newModel;
    if (this.chatOptions.aiModel === 'v1') {
      newAIModel = 'v2';
      newProvider = 'openai';
      newModel = 'gpt-5.1';
    } else {
      newAIModel = 'v1';
      newProvider = 'anthropic';
      newModel = 'claude-sonnet-4-6';
    }

    this.chatOptions.updateClientProviderAndModel(newAIModel, newProvider, newModel);
    
    // Remove the error message
    const errorMessage = this.chatElement.querySelector('.js-error-message');
    if (errorMessage) {
      errorMessage.remove();
    }

    // Add a success message
    this.chatElement.insertAdjacentHTML('beforeend', `
      <div class="alert alert-info js-success-message" role="alert">
        Switched to ${this.getCurrentModel()}. You can continue your conversation.
      </div>
    `);

    this.scrollToBottom();

    // Remove the success message after 5 seconds
    setTimeout(() => {
      const successMessage = this.chatElement.querySelector('.js-success-message');
      if (successMessage) {
        successMessage.remove();
      }
    }, 5000);

    // Resend the last message if it exists
    if (this.lastMessage) {
      this.chatInput.value = this.lastMessage;
      this.handleSubmitForm(this, null);
    }
  }

  async handleListenYourself(button) {
    const messageId = button.dataset.chatMessageId;
    const audioKey = `audioUrl_${messageId}`;
    const audioUrl = localStorage.getItem(audioKey);

    if (this.chatOptions.audioRecordingService.isRecording) {
      this.chatOptions.audioRecordingService.stopRecording();
    }
  

    if (!audioUrl) {
      this.handleInvalidAudio(button, audioKey);
      return;
    }

    let isValidBlob = false
    isValidBlob = await (async () => {
      try {
        const response = await fetch(audioUrl);

        return response.ok;
      } catch (error) {
        return false;
      }
    })();

    if (!isValidBlob) {
      this.handleInvalidAudio(button, audioKey);
      return;
    }

    try {
      this.audioPlayerService.toggleAudio(audioUrl, button);
    } catch (error) {
      this.handleInvalidAudio(button, audioKey);
    }
  }

  handleInvalidAudio(button, audioKey) {
    const message = "This recording of your speech is no longer available. For privacy, we don't save your recordings, which get stored on your device based on its settings.";
    
    const notification = new NotificationService({ message: message, notificationType: 'info' });

    notification.show();
    
    button.classList.add('disabled');
    button.style.opacity = '0.5';
    button.style.cursor = 'not-allowed';

    localStorage.removeItem(audioKey);
  }


  async processMessageText(message, service) {
    let processedMessage = message;
    try {
      processedMessage = await service.processText(message, this.isReadingAidsOn);
    } catch (error) {
      // todo log error 
      return message;
    }
    
    return processedMessage;
  }

  async processLocalChatMessageElement(messageContainer, language) {
    // the messageContainer houses the message bubble element 
    // no element to process, return.
    if (!messageContainer) return;
    if (language === 'chinese' && !await this.ensureChineseTextService()) return;
    // this is for individual messages
    const messageContentEl = messageContainer.querySelector('.chat-message-content');
    const chatMessageDetailEl = messageContentEl.querySelector('.chat-message-detail');
    const chatAlternativeResponseEl = messageContentEl.querySelector('.chat-alternative-response');
    const chatCorrectionExplanationEl = messageContentEl.querySelector('.chat-correction-explanation');
    const chatCorrectionsEl = messageContentEl.querySelector('.chat-corrections');

    if (!chatMessageDetailEl) return;

    const messages = this.getMessages(chatMessageDetailEl, language);
    if (messages.length === 0) return;

    if (chatCorrectionsEl) {
      const corrections = this.getCorrectionExplanationMessages(chatCorrectionsEl, language);
      const correctionsMessagesArray = Array.from(corrections);
      const correctionsMessagesHaveTargetLanguage = this.messagesContainTargetLanguage(correctionsMessagesArray, language);
      if (correctionsMessagesHaveTargetLanguage) {
        const processedCorrections = await this.processSpanElements(corrections, language);
        await this.createAlternativePronunciationView(chatCorrectionsEl, processedCorrections, language);
      }
    }

    if (chatAlternativeResponseEl) {
      const alternativeMessages = this.getAlternativeMessages(chatAlternativeResponseEl, language);
      const alternativeMessagesArray = Array.from(alternativeMessages);
      const alternativeMessagesHaveTargetLanguage = this.messagesContainTargetLanguage(alternativeMessagesArray, language);
      if (alternativeMessagesHaveTargetLanguage) {
        // should get the text from the data-translated-text attribute.
        const processedAlternativeGroups = this.processSpanElements(alternativeMessages, language);
        await this.createAlternativePronunciationView(chatAlternativeResponseEl, processedAlternativeGroups, language);
      }
    }

    const messagesHaveTargetLanguage = this.messagesContainTargetLanguage(messages, language);
    if (messagesHaveTargetLanguage) {
      const processedGroups = this.processSpanElements(messages, language);
      this.renderProcessedGroups(processedGroups, chatMessageDetailEl, messageContentEl, language);
    }
  }

  toggleDetailView(messageContainerEl, show) {
    const chatMessageDetailEl = messageContainerEl.querySelector('.chat-message-detail');
    if (show) {
      chatMessageDetailEl.classList.remove('d-none');
    } else {
      chatMessageDetailEl.classList.add('d-none');
    }
  }
  getAlternativeMessages(chatAlternativeResponseEl, language) {
    if (language === 'japanese') {
      return chatAlternativeResponseEl.querySelectorAll('.js-translatable-text');
    } else if (language === 'chinese') {
      return chatAlternativeResponseEl.querySelectorAll('.js-translatable-text');
    }
    return [];
  }

  getCorrectionExplanationMessages(chatCorrectionExplanationEl, language) {
    if (language === 'japanese') {
      return chatCorrectionExplanationEl.querySelectorAll('.js-translatable-text');
    } else if (language === 'chinese') {
      return chatCorrectionExplanationEl.querySelectorAll('.js-translatable-text');
    }
    return [];
  }

  getMessages(chatMessageDetailEl, language) {
    if (language === 'japanese') {
      const nodes = chatMessageDetailEl.childNodes;
      return Array.from(nodes).filter(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        if (node.tagName === 'BR') return true;
        return node.classList && node.classList.contains('js-translatable-text');
      });
    } else if (language === 'chinese') {
      return getOrderedMessageNodes(chatMessageDetailEl);
    }
    return [];
  }

  messagesContainTargetLanguage(messages, language) {
    if (language === 'japanese') {
      return messages.some(node => 
        node.tagName !== 'BR' && 
        node.classList && 
        node.classList.contains('js-translatable-text') && 
        this.japaneseTextService.containsJapanese(node.textContent)
      );
    } else if (language === 'chinese') {
      return this.chineseTextService
        ? this.chineseTextService.messagesContainsChinese(messages)
        : false;
    }
    return false;
  }

  processSpanElements(messages, language) {
    if (language === 'japanese') {
      return this.japaneseTextService.processSpanElements(messages);
    } else if (language === 'chinese') {
      return this.chineseTextService
        ? this.chineseTextService.processSpanElements(messages)
        : [];
    }
    return [];
  }
  async createAlternativePronunciationView(alternativeEl, processedAlternativeGroups, language) {
    // loop through the processed groups and get the text from the data-translated-text attribute
    processedAlternativeGroups.forEach(async groupItem => {
      // loop over the elements in the groupItem.elements to replace the html with the processed text
      for (const element of groupItem.elements) {
        // use data-phrase to get the text
        const phraseText = element.getAttribute('data-phrase');
        const translatedText = element.getAttribute('data-translated-text') || element.textContent.trimStart().trimEnd();
        const processedText = await this.processText(phraseText || translatedText, language);
        const originalText = phraseText || translatedText;
        this.addProcessedTextToAlternativeEl(processedText, alternativeEl, element, originalText);
      }
    });
    // take the text from the data-translated-text attribute
    // process it
    // replace the html with the processed text
  }
  renderProcessedGroups(processedGroups, chatMessageDetailEl, messageContentEl, language) {
    const newDiv = document.createElement('div');
    newDiv.classList.add('pronunciation-message');
    // Chinese groups split a line at every switch between Chinese and other
    // words, so they run inline and the kept <br>s break the lines (LAN-1316).
    // Japanese groups are whole lines and stay blocks.
    if (language === 'chinese') newDiv.classList.add('pronunciation-message-inline');

    const processingPromises = processedGroups.map(async group => {
      if (group.preserveBoundary) {
        return {
          element: group.elements[0].cloneNode(false),
          index: processedGroups.indexOf(group)
        };
      }

      if (group.translate) {
        const div = document.createElement('div');
        div.classList.add('pronunciation-message-item');
        // Process each element in the group
        for (const element of group.elements) {
          const phraseToTranslate = element.textContent.trimStart().trimEnd();
          const processedText = await this.processText(element.textContent, language);
          
          
          // Copy classes from original element

          // $span.setAttribute('data-phrase', phraseToTranslate);

          // processedText is an array of ruby elements, join them - each ruby element has a ruby and rt element
          // make into a jquery object and add a class for js-translatable-text. 

          this.addProcessedTextToDiv(processedText, div, element, phraseToTranslate);          
        }
        
        return { element: div, index: processedGroups.indexOf(group) };
      } else {
        const translatedDiv = document.createElement('div');
        translatedDiv.classList.add('translated-message');
        
        // Clone each English element and modify its classes
        group.elements.forEach(element => {
          const clonedElement = element.cloneNode(true);
          if (clonedElement.classList.contains('js-translatable-text')) {
            clonedElement.classList.remove('js-translatable-text');
            clonedElement.classList.add('js-translatable-text-no-translation');
          }
          translatedDiv.appendChild(clonedElement);
        });
        
        return { element: translatedDiv, index: processedGroups.indexOf(group) };
      }
    });

    Promise.all(processingPromises).then(results => {
      results.sort((a, b) => a.index - b.index);
      results.forEach(result => newDiv.appendChild(result.element));
      if (chatMessageDetailEl.classList.contains('chat-challenge')) {
        newDiv.classList.add('chat-challenge');
      }
      chatMessageDetailEl.classList.add('d-none');
      // A message can be drawn twice (chat:messageready fires again on the
      // worker's final frame in a text-only chat): replace, don't stack (LAN-1439).
      messageContentEl.querySelectorAll(':scope > .pronunciation-message').forEach(el => el.remove());
      messageContentEl.insertBefore(newDiv, messageContentEl.firstChild);
    
    });
  }
  addProcessedTextToDiv(processedText, div, element, originalText) {
    if (typeof processedText === 'string') {
      const $segment_span = $('<span>');
      $segment_span.append($.parseHTML(processedText));
      element.classList.forEach(cls => {
        $segment_span.addClass(cls);
      });
      $segment_span.attr('data-phrase', originalText);
      div.appendChild($segment_span[0]);
    } else {
      processedText.map(({text, html, hasTranslation}) => {
        const $segment_span = $('<span>');
        $segment_span.append($.parseHTML(html));
        element.classList.forEach(cls => {
          if (hasTranslation && cls === 'js-translatable-text') {
            $segment_span.addClass(cls);
          } else if (cls !== 'js-translatable-text') {
            $segment_span.addClass(cls);
          }

        });
        $segment_span.attr('data-phrase', text);
        div.appendChild($segment_span[0]);
      });
    }
  }

  addProcessedTextToAlternativeEl(processedText, alternativeEl, element, originalText) {
    // alternativeEl will have element, and it needs to be replaced with the processedText spans
    // element is the original element that needs to be replaced
    if (typeof processedText === 'string') {
      const $segment_span = $('<span>');
      $segment_span.append($.parseHTML(processedText));
      element.classList.forEach(cls => {
        $segment_span.addClass(cls);
      });
      $segment_span.attr('data-phrase', originalText);
      alternativeEl.insertBefore($segment_span[0], element);
      element.remove();
    } else {
      let isBoldTagState = 'none';
      const processedTextDiv = document.createElement('div');
      processedText.map(({text, html}) => {
        if (html === '<b>' ) {
          isBoldTagState = 'on';
          return;
        } else if (html === '</b>') {
          isBoldTagState = 'off';
          return;
        }
        const $segment_span = $('<span>');
        switch (isBoldTagState) {
          case 'on':
            $segment_span.append($.parseHTML('<b>'+ html +'</b>'));
            break;
          case 'off':
            $segment_span.append($.parseHTML(html));
            break;
          default:
            $segment_span.append($.parseHTML(html));
            break;
        }
        element.classList.forEach(cls => {
          $segment_span.addClass(cls);
        });
        $segment_span.attr('data-phrase', text);
        processedTextDiv.appendChild($segment_span[0]);
      });
      alternativeEl.insertBefore(processedTextDiv, element);
      element.remove();
    }
  }
  async processText(text, language) {
    if (language === 'japanese') {
      return await this.japaneseTextService.processText(text, this.isReadingAidsOn);
    } else if (language === 'chinese') {
      const service = await this.ensureChineseTextService();
      return service ? service.processText(text, this.isReadingAidsOn) : text;
    }
    return text;
  }

  checkForGuidedModeReply() {
    // Skip if no messages or guided mode is disabled
    const messages = document.querySelectorAll('.chat-content .chat-message:not(.self)');
    if (!messages.length || !this.guidedMode) return;

    // Get the most recent message
    const latestMessage = messages[messages.length - 1];
    const chatMessageId = latestMessage.dataset.chatMessageId;

    // Skip if we've already processed this message
    if (this.lastProcessedMessageId === chatMessageId) return;

    // Skip if waiting for response
    if (this.isWaitingForResponse) return;

    // Get the message text and context
    const messageContent = latestMessage.querySelector('.chat-message-content');
    const text = messageContent.dataset.translatedText;

    // Skip if there's no text content yet (message not fully loaded)
    if (!text) return;

    const lastUserMessage = document.querySelector('.chat-content .chat-message.self:last-child');
    const context = lastUserMessage ? lastUserMessage.querySelector('.chat-message-content').textContent : '';

    // Update the last processed message
    this.lastProcessedMessageId = chatMessageId;
    this.isWaitingForResponse = true;

    // Set a timeout to allow the audio to start playing first
    setTimeout(() => {
      this.guidedModeService.requestSuggestion(chatMessageId, text, context)
        .then(() => {
          this.isWaitingForResponse = false;
        })
        .catch((error) => {
          console.error('Error requesting guided mode suggestion:', error);
          this.isWaitingForResponse = false;
        });
    }, 6000);
  }
}

export default ChatService;
